import React__default, { useRef, useEffect, useState, useCallback, forwardRef, createElement, useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { scroller, createContextHookWithProvider, useOnKeyDown, FETCH_STATUSES, useMedia, getAbsoluteUrl, isNotNullish, parseUrl, getOrigin, isStringGuard, useUpdateEffect, notFalsy, isBrowser, assignRef, convertSrcSet, getImageTypeFromUrl, request, generateNumberArray } from '@tager/web-core';
import Head from 'next/head';
import { useRouter } from 'next/router';
import NextLinkComponent from 'next/link';

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  strings.raw = raw;
  return strings;
}

function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n  top: 0;\n  right: 0;\n  bottom: 0;\n  left: 0;\n  padding: 10px;\n  position: fixed;\n  z-index: 2000;\n  background: rgba(0, 0, 0, 0.8);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n\n  &[hidden] {\n    display: none;\n  }\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}

function Overlay(_ref) {
  var onClose = _ref.onClose,
      hidden = _ref.hidden,
      scrollLockDisabled = _ref.scrollLockDisabled,
      rest = _objectWithoutPropertiesLoose(_ref, ["onClose", "hidden", "scrollLockDisabled"]);

  var ref = useRef(null);
  useEffect(() => {
    if (scrollLockDisabled) return;

    if (!hidden) {
      scroller.lock(ref.current);
    }

    return () => scroller.unlockAll();
  }, [hidden, scrollLockDisabled]);

  function handleClick(event) {
    /** if click occurs on overlay */
    if (event.target === event.currentTarget) {
      onClose();
    }
  }

  return React__default.createElement(Container, Object.assign({
    ref: ref,
    onClick: handleClick,
    hidden: hidden
  }, rest));
}

var Container = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject());

var _createContextHookWit = /*#__PURE__*/createContextHookWithProvider('ModalContext'),
    useCtx = _createContextHookWit[0],
    CtxProvider = _createContextHookWit[1];

function useModal() {
  var context = useCtx();
  return context;
}
var ModalContextProvider = CtxProvider;

function _templateObject3() {
  var data = _taggedTemplateLiteralLoose(["\n          transform: scale(0);\n          opacity: 0;\n        "]);

  _templateObject3 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2() {
  var data = _taggedTemplateLiteralLoose(["\n          transform: scale(1);\n          opacity: 1;\n        "]);

  _templateObject2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$1() {
  var data = _taggedTemplateLiteralLoose(["\n  transition: ", ";\n\n  ", "\n"]);

  _templateObject$1 = function _templateObject() {
    return data;
  };

  return data;
}

function ModalProvider(props) {
  var _ref, _modal$options$withAn, _modal$options, _ref2, _modal$options$compon, _modal$options2, _modal$options2$compo, _props$components, _ref3, _modal$options$scroll, _modal$options3;

  var _useState = useState(null),
      modal = _useState[0],
      setModal = _useState[1];

  var _useState2 = useState(false),
      isInnerContentVisible = _useState2[0],
      setInnerContentVisible = _useState2[1];

  var withAnimation = (_ref = (_modal$options$withAn = modal == null ? void 0 : (_modal$options = modal.options) == null ? void 0 : _modal$options.withAnimation) != null ? _modal$options$withAn : props.withAnimation) != null ? _ref : true;
  var openModal = useCallback(function () {
    return setModal({
      type: arguments.length <= 0 ? undefined : arguments[0],
      props: arguments.length <= 1 ? undefined : arguments[1],
      options: arguments.length <= 2 ? undefined : arguments[2]
    });
  }, []);
  useEffect(function showInnerContentIfModalOpen() {
    if (modal) {
      setInnerContentVisible(true);
    }
  }, [modal]);
  var closeModal = useCallback(function hideInnerContent() {
    setInnerContentVisible(false);

    if (!withAnimation) {
      setModal(null);
    }
  }, [withAnimation]);

  function handleTransitionEnd() {
    if (!isInnerContentVisible) {
      setModal(null);
    }
  }

  useOnKeyDown(['Escape', 'Esc'], () => {
    if (modal) {
      closeModal();
    }
  });
  var ModalOverlay = (_ref2 = (_modal$options$compon = modal == null ? void 0 : (_modal$options2 = modal.options) == null ? void 0 : (_modal$options2$compo = _modal$options2.components) == null ? void 0 : _modal$options2$compo.Overlay) != null ? _modal$options$compon : (_props$components = props.components) == null ? void 0 : _props$components.Overlay) != null ? _ref2 : Overlay;
  var scrollLockDisabled = (_ref3 = (_modal$options$scroll = modal == null ? void 0 : (_modal$options3 = modal.options) == null ? void 0 : _modal$options3.scrollLockDisabled) != null ? _modal$options$scroll : props.scrollLockDisabled) != null ? _ref3 : false;
  var isOpen = Boolean(modal);
  return React__default.createElement(ModalContextProvider, {
    value: openModal
  }, React__default.createElement(ModalOverlay, {
    "data-testid": "modal-overlay",
    "data-modal-overlay": true,
    onClose: closeModal,
    hidden: !isOpen,
    scrollLockDisabled: scrollLockDisabled
  }, React__default.createElement(ModalInner, {
    isOpen: isInnerContentVisible,
    withAnimation: withAnimation,
    onTransitionEnd: handleTransitionEnd
  }, modal ? React__default.createElement(modal.type, {
    closeModal,
    innerProps: modal.props
  }) : null)), props.children);
}

var ModalInner = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject$1(), props => props.withAnimation ? 'transform 0.35s ease-in-out, opacity 0.35s ease-in-out' : 'none', props => props.isOpen ? css(_templateObject2()) : css(_templateObject3()));

var _createContextHookWit$1 = /*#__PURE__*/createContextHookWithProvider('GalleryContext'),
    useCtx$1 = _createContextHookWit$1[0],
    CtxProvider$1 = _createContextHookWit$1[1];

var useGallery = useCtx$1;
var GalleryContextProvider = CtxProvider$1;

function _extends$1() { _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$1(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$1(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

var _ref2 = /*#__PURE__*/createElement("path", {
  d: "M0 0h24v24H0z",
  fill: "none"
});

var _ref3 = /*#__PURE__*/createElement("path", {
  d: "M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"
});

function SvgChevronLeft(_ref, svgRef) {
  var title = _ref.title,
      titleId = _ref.titleId,
      props = _objectWithoutProperties(_ref, ["title", "titleId"]);

  return /*#__PURE__*/createElement("svg", _extends$1({
    xmlns: "http://www.w3.org/2000/svg",
    height: 24,
    viewBox: "0 0 24 24",
    width: 24,
    ref: svgRef,
    "aria-labelledby": titleId
  }, props), title ? /*#__PURE__*/createElement("title", {
    id: titleId
  }, title) : null, _ref2, _ref3);
}

var ForwardRef = /*#__PURE__*/forwardRef(SvgChevronLeft);

function _extends$2() { _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }

function _objectWithoutProperties$1(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$2(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$2(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

var _ref2$1 = /*#__PURE__*/createElement("path", {
  d: "M0 0h24v24H0z",
  fill: "none"
});

var _ref3$1 = /*#__PURE__*/createElement("path", {
  d: "M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"
});

function SvgChevronRight(_ref, svgRef) {
  var title = _ref.title,
      titleId = _ref.titleId,
      props = _objectWithoutProperties$1(_ref, ["title", "titleId"]);

  return /*#__PURE__*/createElement("svg", _extends$2({
    xmlns: "http://www.w3.org/2000/svg",
    height: 24,
    viewBox: "0 0 24 24",
    width: 24,
    ref: svgRef,
    "aria-labelledby": titleId
  }, props), title ? /*#__PURE__*/createElement("title", {
    id: titleId
  }, title) : null, _ref2$1, _ref3$1);
}

var ForwardRef$1 = /*#__PURE__*/forwardRef(SvgChevronRight);

function _extends$3() { _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3.apply(this, arguments); }

function _objectWithoutProperties$2(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$3(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$3(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

var _ref2$2 = /*#__PURE__*/createElement("path", {
  d: "M0 0h24v24H0z",
  fill: "none"
});

var _ref3$2 = /*#__PURE__*/createElement("path", {
  d: "M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
});

function SvgClose(_ref, svgRef) {
  var title = _ref.title,
      titleId = _ref.titleId,
      props = _objectWithoutProperties$2(_ref, ["title", "titleId"]);

  return /*#__PURE__*/createElement("svg", _extends$3({
    xmlns: "http://www.w3.org/2000/svg",
    height: 24,
    viewBox: "0 0 24 24",
    width: 24,
    ref: svgRef,
    "aria-labelledby": titleId
  }, props), title ? /*#__PURE__*/createElement("title", {
    id: titleId
  }, title) : null, _ref2$2, _ref3$2);
}

var ForwardRef$2 = /*#__PURE__*/forwardRef(SvgClose);

function getDisplayedPageNumbers(_ref) {
  var pagesCount = _ref.pagesCount,
      currentPageNumber = _ref.currentPageNumber,
      maxButtonCount = _ref.maxButtonCount;
  var pageNumbers = [];
  var halfCount = Math.round(maxButtonCount / 2);

  for (var i = 1; i <= pagesCount; i++) {
    if (pageNumbers.length < maxButtonCount) {
      pageNumbers.push(i);
    } else {
      /** currentPageIndex - index of current page in result page number list */
      var currentPageIndex = pageNumbers.indexOf(currentPageNumber);

      if (currentPageIndex + 1 > halfCount || currentPageIndex === -1) {
        pageNumbers.shift();
        pageNumbers.push(i);
      }
    }
  }

  return pageNumbers;
}

function _templateObject4() {
  var data = _taggedTemplateLiteralLoose(["\n  animation: ", " 2s linear;\n  animation-iteration-count: infinite;\n  background-image: linear-gradient(\n    to right,\n    rgba(0, 0, 0, 0.08) 0,\n    rgba(0, 0, 0, 0.15) 15%,\n    rgba(0, 0, 0, 0.08) 30%\n  );\n  background-size: 1200px 100%;\n"]);

  _templateObject4 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$1() {
  var data = _taggedTemplateLiteralLoose(["\n0% {\n  background-position: -1200px 0;\n}\n100% {\n  background-position: 1200px 0;\n}\n"]);

  _templateObject3$1 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$1() {
  var data = _taggedTemplateLiteralLoose(["\n  border: 0;\n  clip: rect(0 0 0 0);\n  height: 1px;\n  width: 1px;\n  margin: -1px;\n  padding: 0;\n  overflow: hidden;\n  position: absolute;\n"]);

  _templateObject2$1 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$2() {
  var data = _taggedTemplateLiteralLoose(["\n    @media ", " {\n      ", ";\n    }\n  "]);

  _templateObject$2 = function _templateObject() {
    return data;
  };

  return data;
}
function createMediaQuery(_ref) {
  var min = _ref.min,
      max = _ref.max;

  if (!min && !max && process.env.NODE_ENV === 'development') {
    throw new Error('You didn\'t specify "min" or "max" width for @media mixin');
  }
  /**
   * Note that since browsers do not currently support range context queries,
   * we work around the limitations of min- and max- prefixes and viewports with fractional widths
   * (which can occur under certain conditions on high-dpi devices, for instance)
   * by using values with higher precision for these comparisons (e.g. 767.98px).
   *
   * Reference: "Bootstrap: Responsive breakpoints"
   * https://getbootstrap.com/docs/4.4/layout/overview/#responsive-breakpoints
   *
   * Reference: "Using “min-” and “max-” Prefixes On Range Features"
   * https://www.w3.org/TR/mediaqueries-4/#mq-min-max
   */


  return min && max ? "(min-width: " + min + "px) and (max-width: " + (max - 0.02) + "px)" : min ? "(min-width: " + min + "px)" : max ? "(max-width: " + (max - 0.02) + "px)" : '';
}
function createMediaMixin(params) {
  var mediaQuery = createMediaQuery(params);
  return styles => css(_templateObject$2(), mediaQuery, styles);
}
var visuallyHidden = /*#__PURE__*/css( /*#__PURE__*/_templateObject2$1());
var placeholderAnimation = /*#__PURE__*/keyframes( /*#__PURE__*/_templateObject3$1());
var placeholderAnimationCss = /*#__PURE__*/css( /*#__PURE__*/_templateObject4(), placeholderAnimation);

function _templateObject4$1() {
  var data = _taggedTemplateLiteralLoose(["\n  position: absolute;\n\n  border: ", "px solid\n    ", ";\n  opacity: 1;\n  border-radius: 50%;\n  animation-name: ", ";\n  animation-duration: 1s;\n  animation-timing-function: cubic-bezier(0, 0.2, 0.8, 1);\n  animation-iteration-count: infinite;\n"]);

  _templateObject4$1 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$2() {
  var data = _taggedTemplateLiteralLoose(["\n  from {\n      top: 50%;\n      left: 50%;\n      width: ", "px;\n      height: ", "px;\n      opacity: 1;\n  }\n  \n  to {\n      top: 0;\n      left: 0;\n      width: 100%;\n      height: 100%;\n      opacity: 0;\n  }\n"]);

  _templateObject3$2 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$2() {
  var data = _taggedTemplateLiteralLoose(["\n  display: inline-block;\n  position: relative;\n  width: ", "px;\n  height: ", "px;\n"]);

  _templateObject2$2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$3() {
  var data = _taggedTemplateLiteralLoose(["\n  opacity: ", ";\n  position: absolute;\n  top: 50%;\n  left: 50%;\n  transform: translate(-50%, -50%);\n  pointer-events: none;\n  transition: opacity 0.3s;\n"]);

  _templateObject$3 = function _templateObject() {
    return data;
  };

  return data;
}

function Spinner(props) {
  var _props$size, _props$thickness;

  var size = (_props$size = props.size) != null ? _props$size : 100;
  var thickness = (_props$thickness = props.thickness) != null ? _props$thickness : 4;
  return React__default.createElement(Container$1, {
    visible: Boolean(props.show)
  }, React__default.createElement(Inner, {
    size: size
  }, React__default.createElement(Circle, {
    thickness: thickness,
    color: props.color
  })));
}

var Container$1 = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject$3(), props => props.visible ? 1 : 0);
var Inner = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject2$2(), props => props.size, props => props.size);

function getAnimation(props) {
  return keyframes(_templateObject3$2(), props.thickness * 2, props.thickness * 2);
}

var Circle = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject4$1(), props => props.thickness, props => {
  var _props$color;

  return (_props$color = props.color) != null ? _props$color : 'white';
}, props => getAnimation({
  thickness: props.thickness
}));

function LoadableImage(_ref) {
  var spinnerProps = _ref.spinnerProps,
      spinnerContainerStyle = _ref.spinnerContainerStyle,
      imageProps = _objectWithoutPropertiesLoose(_ref, ["spinnerProps", "spinnerContainerStyle"]);

  var _useState = useState(FETCH_STATUSES.LOADING),
      status = _useState[0],
      setStatus = _useState[1];

  useEffect(() => {
    if (!imageProps.src) return;
    var image = new Image();
    image.addEventListener('load', () => {
      setStatus(FETCH_STATUSES.SUCCESS);
    });
    image.addEventListener('error', () => {
      setStatus(FETCH_STATUSES.FAILURE);
    });
    image.src = imageProps.src;
  }, [imageProps.src]);

  if (status === FETCH_STATUSES.LOADING) {
    return React__default.createElement("div", {
      style: spinnerContainerStyle
    }, React__default.createElement(Spinner, Object.assign({}, spinnerProps, {
      show: true
    })));
  }

  if (status === FETCH_STATUSES.SUCCESS) {
    return React__default.createElement("img", Object.assign({}, imageProps, {
      alt: imageProps.alt
    }));
  }

  return React__default.createElement("div", {
    style: spinnerContainerStyle
  }, React__default.createElement("img", Object.assign({}, imageProps, {
    alt: imageProps.alt || 'Loading error'
  })));
}

function _templateObject16() {
  var data = _taggedTemplateLiteralLoose(["\n  position: absolute;\n  top: 0;\n  right: 0;\n  padding: 1.3rem;\n\n  svg {\n    width: 50px;\n    height: 50px;\n  }\n\n  @media (max-width: 767.98px) {\n    padding: 0.7rem;\n\n    svg {\n      width: 35px;\n      height: 35px;\n    }\n  }\n"]);

  _templateObject16 = function _templateObject16() {
    return data;
  };

  return data;
}

function _templateObject15() {
  var data = _taggedTemplateLiteralLoose(["\n          right: 0;\n          transform: translateX(100%);\n        "]);

  _templateObject15 = function _templateObject15() {
    return data;
  };

  return data;
}

function _templateObject14() {
  var data = _taggedTemplateLiteralLoose(["\n          left: 0;\n          transform: translateX(-100%);\n        "]);

  _templateObject14 = function _templateObject14() {
    return data;
  };

  return data;
}

function _templateObject13() {
  var data = _taggedTemplateLiteralLoose(["\n  position: absolute;\n\n  ", "\n\n  svg {\n    height: 50px;\n    width: 50px;\n  }\n"]);

  _templateObject13 = function _templateObject13() {
    return data;
  };

  return data;
}

function _templateObject12() {
  var data = _taggedTemplateLiteralLoose(["\n          right: 0;\n        "]);

  _templateObject12 = function _templateObject12() {
    return data;
  };

  return data;
}

function _templateObject11() {
  var data = _taggedTemplateLiteralLoose(["\n          left: 0;\n        "]);

  _templateObject11 = function _templateObject11() {
    return data;
  };

  return data;
}

function _templateObject10() {
  var data = _taggedTemplateLiteralLoose(["\n  position: absolute;\n  top: 50%;\n  transform: translateY(-50%);\n\n  ", "\n"]);

  _templateObject10 = function _templateObject10() {
    return data;
  };

  return data;
}

function _templateObject9() {
  var data = _taggedTemplateLiteralLoose(["\n  transition: background-color 0.1s;\n\n  svg {\n    display: block;\n    height: 96px;\n    width: 96px;\n    fill: white;\n    transition: opacity 0.1s;\n    opacity: 0.9;\n\n    @media (max-width: 1023.98px) {\n      width: 80px;\n      height: 80px;\n    }\n\n    @media (max-width: 767.98px) {\n      width: 50px;\n      height: 50px;\n    }\n  }\n\n  &:disabled {\n    opacity: 0.3;\n  }\n\n  &:not(:disabled):hover {\n    background-color: rgba(255, 255, 255, 0.1);\n\n    svg {\n      opacity: 1;\n    }\n  }\n"]);

  _templateObject9 = function _templateObject9() {
    return data;
  };

  return data;
}

function _templateObject8() {
  var data = _taggedTemplateLiteralLoose(["\n  flex: 1 1 auto;\n  display: block;\n  padding: 0.5rem;\n  color: white;\n  opacity: 0.9;\n\n  @media (max-width: 767.98px) {\n    font-size: 0.8em;\n  }\n"]);

  _templateObject8 = function _templateObject8() {
    return data;
  };

  return data;
}

function _templateObject7() {
  var data = _taggedTemplateLiteralLoose(["\n  flex: 0 0 auto;\n  display: block;\n  padding: 0.5rem;\n  color: white;\n  opacity: 0.7;\n  font-size: 0.9em;\n\n  @media (max-width: 767.98px) {\n    font-size: 0.7em;\n  }\n"]);

  _templateObject7 = function _templateObject7() {
    return data;
  };

  return data;
}

function _templateObject6() {
  var data = _taggedTemplateLiteralLoose(["\n  position: absolute;\n  width: 100%;\n  display: flex;\n  justify-content: space-between;\n"]);

  _templateObject6 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5() {
  var data = _taggedTemplateLiteralLoose(["\n          transform: translateY(-7px);\n        "]);

  _templateObject5 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$2() {
  var data = _taggedTemplateLiteralLoose(["\n  position: relative;\n  margin: 2px;\n  cursor: pointer;\n  border-radius: 2px;\n  overflow: hidden;\n  transition: transform 0.2s ease-in-out;\n  font-size: 10px;\n  color: white;\n\n  ", "\n  img {\n    z-index: -1;\n    height: 50px;\n    width: 50px;\n    object-fit: cover;\n  }\n"]);

  _templateObject4$2 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$3() {
  var data = _taggedTemplateLiteralLoose(["\n  color: white;\n  font-size: 1.5rem;\n  img {\n    height: 100%;\n    width: 100%;\n    object-fit: contain;\n    max-height: calc(100vh - 290px);\n    max-width: 1024px;\n  }\n"]);

  _templateObject3$3 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$3() {
  var data = _taggedTemplateLiteralLoose(["\n  position: relative;\n  user-select: none;\n  max-width: calc(100vw - 192px);\n\n  @media (max-width: 1023.98px) {\n    max-width: calc(100vw - 160px);\n  }\n  @media (max-width: 767.98px) {\n    max-width: calc(100vw - 100px);\n  }\n"]);

  _templateObject2$3 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$4() {
  var data = _taggedTemplateLiteralLoose(["\n  position: absolute;\n  bottom: 0;\n  left: 50%;\n  transform: translate(-50%);\n  display: flex;\n"]);

  _templateObject$4 = function _templateObject() {
    return data;
  };

  return data;
}

function Gallery(_ref) {
  var _imageList$currentInd, _imageList$currentInd2;

  var options = _ref.options,
      onClose = _ref.onClose;
  var imageList = options.imageList,
      initialIndex = options.initialIndex;
  var isMobile = useMedia(createMediaQuery({
    max: 767.98
  }));

  var _useState = useState(initialIndex || 0),
      currentIndex = _useState[0],
      setCurrentIndex = _useState[1];

  function prev() {
    setCurrentIndex(prev => prev !== 0 ? prev - 1 : prev);
  }

  var next = useCallback(() => setCurrentIndex(prev => prev < imageList.length - 1 ? prev + 1 : prev), [imageList.length]);
  useOnKeyDown(['ArrowLeft', 'Left'], prev);
  useOnKeyDown(['ArrowRight', 'Right'], next);
  useOnKeyDown(['Escape', 'Esc'], onClose);
  var displayedImageIndexList = useMemo(() => getDisplayedPageNumbers({
    pagesCount: imageList.length,
    currentPageNumber: currentIndex + 1,
    maxButtonCount: isMobile ? 3 : 5
  }).map(number => number - 1), [currentIndex, imageList.length, isMobile]);
  return React__default.createElement(Overlay, {
    onClose: onClose
  }, React__default.createElement(Container$2, null, React__default.createElement(ImageContainer, null, React__default.createElement(LoadableImage, {
    src: (_imageList$currentInd = imageList[currentIndex]) == null ? void 0 : _imageList$currentInd.url,
    spinnerContainerStyle: {
      width: '50vw',
      height: '50vh'
    },
    alt: ""
  })), React__default.createElement(ImageFooter, null, React__default.createElement(Caption, null, (_imageList$currentInd2 = imageList[currentIndex]) == null ? void 0 : _imageList$currentInd2.caption), React__default.createElement(PageNumber, null, currentIndex + 1, " of ", imageList.length))), React__default.createElement(SmallGallery, null, imageList.map((image, index) => React__default.createElement(SmallImageContainer, {
    key: image.url,
    onClick: () => setCurrentIndex(index),
    isActive: index === currentIndex,
    hidden: !displayedImageIndexList.includes(index)
  }, React__default.createElement(LoadableImage, {
    src: image.url,
    alt: "",
    spinnerContainerStyle: {
      width: 50,
      height: 50
    },
    spinnerProps: {
      size: 35,
      thickness: 2
    }
  }))), React__default.createElement(SmallArrowButton, {
    direction: "left",
    title: "Previous",
    onClick: prev,
    disabled: currentIndex === 0
  }, React__default.createElement(ForwardRef, null)), React__default.createElement(SmallArrowButton, {
    direction: "right",
    title: "Next",
    onClick: next,
    disabled: currentIndex === imageList.length - 1
  }, React__default.createElement(ForwardRef$1, null))), React__default.createElement(CloseButton, {
    type: "button",
    title: "Close",
    onClick: onClose
  }, React__default.createElement(ForwardRef$2, null)), React__default.createElement(ArrowButton, {
    direction: "left",
    title: "Previous",
    onClick: prev,
    disabled: currentIndex === 0
  }, React__default.createElement(ForwardRef, null)), React__default.createElement(ArrowButton, {
    direction: "right",
    title: "Next",
    onClick: next,
    disabled: currentIndex === imageList.length - 1
  }, React__default.createElement(ForwardRef$1, null)));
}

var SmallGallery = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject$4());
var Container$2 = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject2$3());
var ImageContainer = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject3$3());
var SmallImageContainer = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject4$2(), props => props.isActive ? css(_templateObject5()) : null);
var ImageFooter = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject6());
var PageNumber = /*#__PURE__*/styled.span( /*#__PURE__*/_templateObject7());
var Caption = /*#__PURE__*/styled.span( /*#__PURE__*/_templateObject8());
var NavButton = /*#__PURE__*/styled.button( /*#__PURE__*/_templateObject9());
var ArrowButton = /*#__PURE__*/styled(NavButton)( /*#__PURE__*/_templateObject10(), props => props.direction === 'left' ? css(_templateObject11()) : css(_templateObject12()));
var SmallArrowButton = /*#__PURE__*/styled(NavButton)( /*#__PURE__*/_templateObject13(), props => props.direction === 'left' ? css(_templateObject14()) : css(_templateObject15()));
var CloseButton = /*#__PURE__*/styled(NavButton)( /*#__PURE__*/_templateObject16());

function GalleryProvider(props) {
  var _useState = useState({
    isOpen: false,
    options: null
  }),
      state = _useState[0],
      setState = _useState[1];

  var openGallery = useCallback(options => setState({
    isOpen: true,
    options
  }), []);
  var closeGallery = useCallback(() => setState({
    isOpen: false,
    options: null
  }), []);

  function renderChildren() {
    if (typeof props.children === 'function') {
      return props.children(openGallery);
    }

    return props.children;
  }

  return React__default.createElement(GalleryContextProvider, {
    value: openGallery
  }, state.isOpen ? React__default.createElement(Gallery, {
    onClose: closeGallery,
    options: state.options
  }) : null, renderChildren());
}

/**
 * References:
 * 1. https://support.google.com/webmasters/answer/139066
 * 2. https://yoast.com/rel-canonical/
 */

function getCanonicalUrl(currentPath, canonicalPath) {
  var absoluteUrl = getAbsoluteUrl(canonicalPath != null ? canonicalPath : currentPath);
  var parsedUrl = parseUrl(absoluteUrl);
  /** Remove hash from url */

  return parsedUrl ? parsedUrl.origin + parsedUrl.pathname + parsedUrl.search : absoluteUrl;
}
function getLdJsonData(currentPath, title, description, image, datePublished, dateModified, organizationName, logoSrc) {
  var currentUrl = getAbsoluteUrl(currentPath);
  var result = {
    '@context': 'http://schema.org',
    '@type': 'WebPage'
  };

  if (title) {
    result.name = title;
  }

  if (description) {
    result.description = description;
  }

  if (image) {
    result.image = [getAbsoluteUrl(image)];
  }

  if (datePublished) {
    result.datePublished = datePublished;
  }

  if (dateModified) {
    result.dateModified = dateModified;
  }

  if (organizationName) {
    result.publisher = {
      '@type': 'Organization',
      name: organizationName
    };

    if (process.env.NEXT_PUBLIC_ORIGIN) {
      result.publisher.url = process.env.NEXT_PUBLIC_ORIGIN;
    }

    if (logoSrc) {
      result.publisher.logo = {
        '@type': 'ImageObject',
        url: [getAbsoluteUrl(logoSrc)]
      };
    }
  }

  if (currentUrl) {
    result.mainEntityOfPage = {
      '@type': 'WebPage',
      '@id': getAbsoluteUrl(currentUrl)
    };
  }

  return result;
}
function getMetaList(_ref) {
  var title = _ref.title,
      description = _ref.description,
      keywords = _ref.keywords,
      openGraphTitle = _ref.openGraphTitle,
      openGraphDescription = _ref.openGraphDescription,
      openGraphImage = _ref.openGraphImage,
      currentPath = _ref.currentPath;
  var currentPageUrl = getAbsoluteUrl(currentPath);
  var ogTitle = openGraphTitle || title;
  var ogDescription = openGraphDescription || description;
  return [
  /**
   * HTML Living Standard
   * The definition of '<meta>' in that specification.
   * https://html.spec.whatwg.org/multipage/semantics.html#the-meta-element
   */
  {
    name: 'copyright',
    content: 'OZiTAG LLC'
  }, {
    name: 'author',
    content: 'OZiTAG, ozitag.com'
  }, description ? {
    name: 'description',
    content: description
  } : null, keywords ? {
    name: 'keywords',
    content: keywords
  } : null,
  /**
   * Metadata for Open Graph protocol
   * Reference: https://ogp.me/
   */
  currentPageUrl ? {
    name: 'og:url',
    content: currentPageUrl
  } : null, ogTitle ? {
    property: 'og:title',
    content: ogTitle
  } : null, ogDescription ? {
    property: 'og:description',
    content: ogDescription
  } : null, openGraphImage ? {
    property: 'og:image',
    content: getAbsoluteUrl(openGraphImage)
  } : null, {
    property: 'og:type',
    content: 'website'
  },
  /**
   * Metadata for Twitter cards
   * Reference: https://developer.twitter.com/en/docs/tweets/optimize-with-cards/overview/markup
   */
  {
    name: 'twitter:card',
    content: 'summary'
  }, {
    name: 'twitter:creator',
    content: 'OZiTAG, ozitag.com'
  }, ogTitle ? {
    name: 'twitter:title',
    content: ogTitle
  } : null, ogDescription ? {
    name: 'twitter:description',
    content: ogDescription
  } : null, openGraphImage ? {
    name: 'twitter:image',
    content: getAbsoluteUrl(openGraphImage)
  } : null].filter(isNotNullish);
}

function Page(_ref) {
  var children = _ref.children,
      title = _ref.title,
      description = _ref.description,
      keywords = _ref.keywords,
      openGraphTitle = _ref.openGraphTitle,
      openGraphDescription = _ref.openGraphDescription,
      openGraphImage = _ref.openGraphImage,
      canonicalUrl = _ref.canonicalUrl,
      datePublished = _ref.datePublished,
      dateModified = _ref.dateModified;
  var router = useRouter();
  var metaList = getMetaList({
    title,
    description,
    keywords,
    openGraphTitle,
    openGraphDescription,
    openGraphImage,
    currentPath: router.asPath
  });
  var canonicalUrlPrepared = getCanonicalUrl(router.asPath, canonicalUrl);
  var homePageUrl = getOrigin();
  var jsonLdObject = getLdJsonData(router.asPath, title, description, openGraphImage, datePublished, dateModified, 'OZiTAG', '/logo.svg');
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(Head, null, React__default.createElement("title", null, title != null ? title : ''), homePageUrl ? React__default.createElement("link", {
    href: homePageUrl,
    rel: "home"
  }) : null, canonicalUrlPrepared ? React__default.createElement("link", {
    href: canonicalUrlPrepared,
    rel: "canonical"
  }) : null, metaList.map((metaProps, index) => React__default.createElement("meta", Object.assign({}, metaProps, {
    key: index
  }))), jsonLdObject ? React__default.createElement("script", {
    type: "application/ld+json",
    dangerouslySetInnerHTML: {
      __html: JSON.stringify(jsonLdObject)
    }
  }) : null), children);
}

function isPreloaderEnabled() {
  var isEnabledEnv = process.env.NEXT_PUBLIC_SPLASHSCREEN_ENABLED;
  return isStringGuard(isEnabledEnv) && (isEnabledEnv.toLowerCase() === 'true' || isEnabledEnv === '1');
}

function _templateObject6$1() {
  var data = _taggedTemplateLiteralLoose(["\n  position: absolute;\n  display: inline-block;\n  top: 0;\n  background-color: #f7f7f7;\n  border-radius: 100%;\n  width: 35px;\n  height: 35px;\n  animation: ", " 2s infinite ease-in-out;\n\n  &:last-child {\n    top: auto;\n    bottom: 0;\n    animation-delay: -1s;\n  }\n"]);

  _templateObject6$1 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5$1() {
  var data = _taggedTemplateLiteralLoose(["\n  position: relative;\n  top: 50%;\n  left: 50%;\n  width: 70px;\n  height: 70px;\n  margin-top: -35px;\n  margin-left: -35px;\n  text-align: center;\n  animation: ", " 2s infinite linear;\n"]);

  _templateObject5$1 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$3() {
  var data = _taggedTemplateLiteralLoose(["\n  position: fixed;\n  top: 0;\n  left: 0;\n  bottom: 0;\n  right: 0;\n  background: #fff;\n  opacity: 1;\n  will-change: opacity;\n  z-index: 1000;\n\n  &.fade-out {\n    animation: ", " 1s ease-in-out forwards;\n  }\n"]);

  _templateObject4$3 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$4() {
  var data = _taggedTemplateLiteralLoose(["\n  0% {\n    opacity: 1;\n  }\n\n  100% {\n    opacity: 0;\n  }\n"]);

  _templateObject3$4 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$4() {
  var data = _taggedTemplateLiteralLoose(["\n  0%,\n  100% {\n    transform: scale(0);\n  }\n\n  50% {\n    transform: scale(1);\n  }\n"]);

  _templateObject2$4 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$5() {
  var data = _taggedTemplateLiteralLoose(["\n  100% {\n    transform: rotate(360deg);\n  }\n"]);

  _templateObject$5 = function _templateObject() {
    return data;
  };

  return data;
}
var rotateCss = /*#__PURE__*/keyframes( /*#__PURE__*/_templateObject$5());
var bounceCss = /*#__PURE__*/keyframes( /*#__PURE__*/_templateObject2$4());
var fadeOutCss = /*#__PURE__*/keyframes( /*#__PURE__*/_templateObject3$4());
var Overlay$1 = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject4$3(), fadeOutCss);
var Inner$1 = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject5$1(), rotateCss);
var Item = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject6$1(), bounceCss);

function Preloader(_ref) {
  var hiddenProp = _ref.hidden,
      className = _ref.className,
      debug = _ref.debug;
  var isControlled = hiddenProp !== undefined;

  function isInitiallyVisible() {
    var isVisible = isBrowser() ? isPreloaderEnabled() && !window.isPreloaderHidden : isPreloaderEnabled();
    return isControlled ? !hiddenProp : isVisible;
  }

  var _useState = useState(isInitiallyVisible() ? 'VISIBLE' : 'HIDDEN'),
      status = _useState[0],
      setStatus = _useState[1];

  useEffect(() => {
    if (!debug) return;
    console.log("%c [DEBUG Preloader]: isControlled - " + isControlled, 'color: green');
    console.log("%c [DEBUG Preloader]: isInitiallyVisible - " + isInitiallyVisible(), 'color: green'); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debug]);
  useEffect(() => {
    if (!debug) return;
    console.log("%c [DEBUG Preloader]: status - " + status, 'color: green');
  }, [debug, status]);

  function handleAnimationEnd() {
    setStatus('HIDDEN');
  }

  useUpdateEffect(() => {
    if (isControlled) {
      setStatus(hiddenProp ? 'FADING_OUT' : 'VISIBLE');
    }
  }, [hiddenProp, isControlled]);
  useEffect(() => {
    if (isControlled || status !== 'VISIBLE') return;

    function hidePreloader() {
      setStatus('FADING_OUT');
      window.isPreloaderHidden = true;
    }

    if (document.readyState !== 'loading') {
      hidePreloader();
      return;
    }

    document.addEventListener('DOMContentLoaded', hidePreloader);
    return () => {
      document.removeEventListener('DOMContentLoaded', hidePreloader);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isControlled]);

  if (status === 'HIDDEN') {
    return null;
  }

  var containerClassName = [status === 'FADING_OUT' ? 'fade-out' : undefined, className].filter(notFalsy).join(' ');
  return React__default.createElement(Overlay$1, {
    className: containerClassName,
    onAnimationEnd: handleAnimationEnd,
    "data-preloader-overlay": true
  }, React__default.createElement(Inner$1, {
    "data-preloader": true
  }, React__default.createElement(Item, {
    "data-preloader-item": true
  }), React__default.createElement(Item, {
    "data-preloader-item": true
  })));
}

/**
 * Placeholder fixes broken image symbol: {@link https://github.com/aFarkas/lazysizes#broken-image-symbol}
 *
 * Reference: {@link https://github.com/verlok/vanilla-lazyload/issues/212#issuecomment-397807313}
 *
 * If you don't put anything in the `src`,
 * when `lazysizes` copies the `data-src` in the `src`,
 * the image is shown while loading by the browser.
 *
 * If you put something in the `src`,
 * when `lazysizes` copies the `data-src` in the `src`,
 * the browser waits until the new image is loaded, then replaces the image.
 * That what we do when images are loaded lazily.
 */

var IMAGE_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
var Image$1 = /*#__PURE__*/React__default.forwardRef(function ImageWithRef(_ref, ref) {
  var className = _ref.className,
      src = _ref.src,
      srcSet = _ref.srcSet,
      _ref$loading = _ref.loading,
      loading = _ref$loading === void 0 ? 'eager' : _ref$loading,
      rest = _objectWithoutPropertiesLoose(_ref, ["className", "src", "srcSet", "loading"]);

  var isLazy = loading === 'lazy';
  var imgClassName = [className, isLazy ? 'lazyload' : null].filter(Boolean).join(' ');
  return React__default.createElement("img", Object.assign({
    className: imgClassName,
    ref: ref,
    src: isLazy ? IMAGE_PLACEHOLDER : src,
    srcSet: isLazy ? undefined : srcSet,
    "data-src": src,
    "data-srcset": srcSet,
    alt: ""
  }, rest));
});

function Source(_ref) {
  var _ref2, _srcList$;

  var srcList = _ref.srcList,
      isLazy = _ref.isLazy,
      type = _ref.type,
      rest = _objectWithoutPropertiesLoose(_ref, ["srcList", "isLazy", "type"]);

  if (srcList.length === 0) return null;
  return React__default.createElement("source", Object.assign({}, rest, {
    srcSet: isLazy ? undefined : convertSrcSet(srcList),
    "data-srcset": isLazy ? convertSrcSet(srcList) : undefined,
    type: (_ref2 = type != null ? type : getImageTypeFromUrl((_srcList$ = srcList[0]) != null ? _srcList$ : null)) != null ? _ref2 : undefined
  }));
}

function getSrcList(src1x, src2x) {
  var srcList = [];

  if (src1x) {
    srcList.push(src1x);

    if (src2x) {
      srcList.push(src2x);
    }
  }

  return srcList;
}

function SourceGroup(_ref3) {
  var media = _ref3.media,
      images = _ref3.images,
      isLazy = _ref3.isLazy;
  if (!images || Object.values(images).length === 0) return null;
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(Source, {
    isLazy: isLazy,
    srcList: getSrcList(images.webp, images.webp2x),
    media: media
  }), React__default.createElement(Source, {
    isLazy: isLazy,
    srcList: getSrcList(images.src, images.src2x),
    media: media
  }));
}

function Picture(_ref4) {
  var src = _ref4.src,
      src2x = _ref4.src2x,
      srcWebp = _ref4.srcWebp,
      srcWebp2x = _ref4.srcWebp2x,
      alt = _ref4.alt,
      className = _ref4.className,
      imageStyle = _ref4.imageStyle,
      loading = _ref4.loading,
      outerImageRef = _ref4.imageRef,
      mediaQueryList = _ref4.mediaQueryList,
      imageMap = _ref4.imageMap,
      onStatusChange = _ref4.onStatusChange;
  var isLazy = loading === 'lazy';
  var innerImageRef = useRef(null);

  var _useState = useState(FETCH_STATUSES.IDLE),
      status = _useState[0],
      setStatus = _useState[1];

  var statusChangeHandler = useRef(onStatusChange);
  useEffect(function trackImageLoadStatus() {
    if (!innerImageRef.current) return;
    /** If Image is already loaded */

    if (innerImageRef.current.src && innerImageRef.current.complete && IMAGE_PLACEHOLDER !== innerImageRef.current.src) {
      setStatus(FETCH_STATUSES.SUCCESS);
      return;
    }
    /** If `lazysizes` didn't change `src` attribute yet */


    if (IMAGE_PLACEHOLDER === innerImageRef.current.src) {
      var mutationObserver = new MutationObserver(function mutationCallback(_mutations, observer) {
        setStatus(FETCH_STATUSES.LOADING);
        observer.disconnect();
        if (!innerImageRef.current) return;

        if (innerImageRef.current.complete) {
          setStatus(FETCH_STATUSES.SUCCESS);
          return;
        }

        function handleLoad() {
          if (!innerImageRef.current) return;
          innerImageRef.current.removeEventListener('load', handleLoad);
          setStatus(FETCH_STATUSES.SUCCESS);
        }

        function handleError() {
          if (!innerImageRef.current) return;
          innerImageRef.current.removeEventListener('error', handleError);
          setStatus(FETCH_STATUSES.FAILURE);
        }

        innerImageRef.current.addEventListener('load', handleLoad);
        innerImageRef.current.addEventListener('error', handleError);
      });
      mutationObserver.observe(innerImageRef.current, {
        attributes: true,
        attributeFilter: ['src']
      });
      return;
    }
    /** If image is currently loading */


    if (innerImageRef.current.src && innerImageRef.current.src !== IMAGE_PLACEHOLDER) {
      setStatus(FETCH_STATUSES.LOADING);

      var handleLoad = () => {
        if (!innerImageRef.current) return;
        innerImageRef.current.removeEventListener('load', handleLoad);
        setStatus(FETCH_STATUSES.SUCCESS);
      };

      var handleError = () => {
        if (!innerImageRef.current) return;
        innerImageRef.current.removeEventListener('error', handleError);
        setStatus(FETCH_STATUSES.FAILURE);
      };

      innerImageRef.current.addEventListener('load', handleLoad);
      innerImageRef.current.addEventListener('error', handleError);
    }
  }, []);
  useEffect(function updateStatusChangeHandlerRef() {
    statusChangeHandler.current = onStatusChange;
  }, [onStatusChange]);
  useEffect(function callStatusChangeHandler() {
    if (statusChangeHandler.current) {
      statusChangeHandler.current(status);
    }
  }, [status]);
  return React__default.createElement("picture", {
    className: className
  }, mediaQueryList.map(mediaQuery => React__default.createElement(SourceGroup, {
    key: mediaQuery.name,
    media: mediaQuery.value,
    images: imageMap[mediaQuery.name],
    isLazy: isLazy
  })), src2x || srcWebp || srcWebp2x ? React__default.createElement(SourceGroup, {
    images: {
      src: src,
      src2x: src2x,
      webp: srcWebp,
      webp2x: srcWebp2x
    },
    isLazy: isLazy
  }) : null, React__default.createElement(Image$1, {
    style: imageStyle,
    src: src != null ? src : undefined,
    srcSet: src2x ? src2x + " 2x" : undefined,
    loading: loading,
    alt: alt != null ? alt : '',
    ref: imageNode => {
      assignRef(innerImageRef, imageNode);
      assignRef(outerImageRef, imageNode);
    },
    "data-image-status": status.toLowerCase()
  }));
}

function dedupeMediaQueryList(list) {
  var map = new Map();
  list.forEach(item => map.set(item.name, item));
  /**
   * The Map remembers the original insertion order of the keys.
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Map
   */

  return Array.from(map.values());
}

function createPlainPictureComponent(options) {
  var uniqueMediaQueryList = dedupeMediaQueryList(options.mediaQueryList);

  function PlainPicture(props) {
    var imageMap = uniqueMediaQueryList.reduce((map, mediaQuery) => _extends({}, map, {
      [mediaQuery.name]: props[mediaQuery.name]
    }), {});
    return React__default.createElement(Picture, Object.assign({}, props, {
      mediaQueryList: uniqueMediaQueryList,
      imageMap: imageMap
    }));
  }

  return PlainPicture;
}

function _templateObject$6() {
  var data = _taggedTemplateLiteralLoose(["\n  position: relative;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  transition: background-color 0.3s;\n  background-color: ", ";\n\n  img {\n    transition: opacity 0.3s;\n  }\n"]);

  _templateObject$6 = function _templateObject() {
    return data;
  };

  return data;
}
function createPictureComponent(options) {
  var PlainPicture = createPlainPictureComponent(options);

  function SmartPicture(_ref) {
    var className = _ref.className,
        width = _ref.width,
        height = _ref.height,
        useSpinner = _ref.useSpinner,
        usePlaceholder = _ref.usePlaceholder,
        placeholderColor = _ref.placeholderColor,
        SpinnerComponent = _ref.spinnerComponent,
        onStatusChange = _ref.onStatusChange,
        plainPictureProps = _objectWithoutPropertiesLoose(_ref, ["className", "width", "height", "useSpinner", "usePlaceholder", "placeholderColor", "spinnerComponent", "onStatusChange"]);

    var _useState = useState(FETCH_STATUSES.IDLE),
        status = _useState[0],
        setStatus = _useState[1];

    var isLoading = status === FETCH_STATUSES.LOADING;

    function renderSpinner() {
      return SpinnerComponent ? React__default.createElement(SpinnerComponent, null) : React__default.createElement(Spinner, {
        show: true
      });
    }

    var containerStyle = useMemo(() => width !== undefined && height !== undefined ? {
      width,
      height
    } : undefined, [height, width]);
    var imageStyle = useMemo(() => isLoading && (useSpinner || usePlaceholder) ? {
      opacity: 0
    } : undefined, [isLoading, usePlaceholder, useSpinner]);

    function handleStatusChange(status) {
      setStatus(status);

      if (onStatusChange) {
        onStatusChange(status);
      }
    }

    return React__default.createElement(PictureContainer, {
      className: className,
      style: containerStyle,
      backgroundColor: isLoading && usePlaceholder ? placeholderColor : undefined,
      "data-picture-loading": isLoading,
      "data-picture-status": status.toLowerCase()
    }, isLoading && useSpinner ? renderSpinner() : null, React__default.createElement(PlainPicture, Object.assign({}, plainPictureProps, {
      onStatusChange: handleStatusChange,
      imageStyle: imageStyle
    })));
  }

  return SmartPicture;
}
var PictureContainer = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject$6(), props => {
  var _props$backgroundColo;

  return (_props$backgroundColo = props.backgroundColor) != null ? _props$backgroundColo : 'transparent';
});

function _templateObject$7() {
  var data = _taggedTemplateLiteralLoose(["\n  cursor: ", ";\n"]);

  _templateObject$7 = function _templateObject() {
    return data;
  };

  return data;
}

function isLinkActive(to, router) {
  if (typeof to === 'string') {
    return to === router.pathname;
  } else {
    return (to == null ? void 0 : to.as) === router.asPath;
  }
}

function isRenderFunction(children) {
  return typeof children === 'function';
}

var DefaultLinkComponent = /*#__PURE__*/styled.a( /*#__PURE__*/_templateObject$7(), props => props.isActive || props.disabled ? 'default' : 'pointer');
/**
 * Source: https://blog.logrocket.com/dealing-with-links-in-next-js/
 */

function createLinkComponent(options) {
  if (options === void 0) {
    options = {};
  }

  var _options = options,
      _options$nextLinkComp = _options.nextLinkComponent,
      NextLink = _options$nextLinkComp === void 0 ? NextLinkComponent : _options$nextLinkComp,
      _options$uiLinkCompon = _options.uiLinkComponent,
      DefaultLink = _options$uiLinkCompon === void 0 ? DefaultLinkComponent : _options$uiLinkCompon;
  return React__default.forwardRef((_ref, ref) => {
    var to = _ref.to,
        replace = _ref.replace,
        scroll = _ref.scroll,
        shallow = _ref.shallow,
        _ref$passHref = _ref.passHref,
        passHref = _ref$passHref === void 0 ? true : _ref$passHref,
        prefetch = _ref.prefetch,
        className = _ref.className,
        activeClassName = _ref.activeClassName,
        isActiveProp = _ref.isActive,
        disabled = _ref.disabled,
        children = _ref.children,
        restLinkProps = _objectWithoutPropertiesLoose(_ref, ["to", "replace", "scroll", "shallow", "passHref", "prefetch", "className", "activeClassName", "isActive", "disabled", "children"]);

    /** router is null in Storybook environment */
    var router = useRouter();
    var convertedTo = useMemo(() => options.converter ? options.converter(to) : to, [to]);
    var isActive = useMemo(() => {
      if (!router) return false;

      if (typeof isActiveProp === 'function') {
        return isActiveProp();
      } else if (typeof isActiveProp === 'boolean') {
        return isActiveProp;
      } else {
        return isLinkActive(convertedTo, router);
      }
    }, [isActiveProp, router, convertedTo]);
    var linkClassName = [isActive ? activeClassName : null, className].filter(Boolean).join(' ');

    function onClick(event) {
      var path = typeof convertedTo === 'string' ? convertedTo : convertedTo == null ? void 0 : convertedTo.as;

      if (!router || router.asPath === path || disabled) {
        event.preventDefault();
      }
    }

    function renderLink() {
      var linkProps = _extends({}, restLinkProps, {
        className: linkClassName,
        isActive,
        onClick,
        ref,
        disabled
      });

      if (isRenderFunction(children)) {
        return children(linkProps);
      } else {
        /** Use can override this component via "as" prop */
        return React__default.createElement(DefaultLink, Object.assign({}, linkProps), children);
      }
    }

    var route = isNotNullish(convertedTo) && typeof convertedTo === 'object' ? convertedTo : {
      href: convertedTo != null ? convertedTo : ''
    };
    /** otherwise pass both "href" / "as" */

    return React__default.createElement(NextLink, Object.assign({}, route, {
      replace: replace,
      scroll: scroll,
      shallow: shallow,
      passHref: disabled ? false : passHref,
      prefetch: prefetch
    }), renderLink());
  });
}

/**
 * Reference - Screen Resolution Stats Worldwide:
 * https://gs.statcounter.com/screen-resolution-stats
 *
 * 16px - scrollbar width on Windows,
 * some browsers doesn't include scrollbar width when calculate media queries
 **/
var breakpoints = {
  /** iPhone 5/SE */
  mobileSmall: 320,

  /** iPhone 6/7/8/X */
  mobileMedium: 375,

  /** iPhone 6/7/8 Plus */
  mobileLarge: 414,

  /** iPad 1, 2, Mini and Air */
  tabletSmall: 768,
  tabletLarge: 1024,

  /** 1280 - 16 = 1264 -> 1260 - more beautiful number :) */
  laptop: 1260,

  /** 1536 - 16 = 1520 -> 1500 - more beautiful number :) */
  desktop: 1500
};
/**
 * Reference: https://get.foundation/sites/docs/media-queries.html#working-with-media-queries
 *
 * Media queries name convention: {breakpoint_name + modifier_name}
 * Modifiers: "up" | "only" | "down" (default)
 * "down" is default modifier, because we use desktop-first approach
 *
 * Examples:
 *
 * "tablet", "tabletDown" - tablet or smaller
 * "tabletOnly" - only tablet
 * "tabletUp" - tablet or larger
 */

var media = {
  mobileSmall: /*#__PURE__*/createMediaMixin({
    max: breakpoints.mobileMedium
  }),
  mobileMedium: /*#__PURE__*/createMediaMixin({
    max: breakpoints.mobileLarge
  }),
  mobileMediumOnly: /*#__PURE__*/createMediaMixin({
    min: breakpoints.mobileSmall,
    max: breakpoints.mobileLarge
  }),
  mobileLarge: /*#__PURE__*/createMediaMixin({
    max: breakpoints.tabletSmall
  }),
  mobileLargeOnly: /*#__PURE__*/createMediaMixin({
    min: breakpoints.mobileLarge,
    max: breakpoints.tabletSmall
  }),
  mobile: /*#__PURE__*/createMediaMixin({
    max: breakpoints.tabletSmall
  }),
  noMobile: /*#__PURE__*/createMediaMixin({
    min: breakpoints.tabletSmall
  }),
  tabletSmall: /*#__PURE__*/createMediaMixin({
    max: breakpoints.tabletLarge
  }),
  tabletSmallOnly: /*#__PURE__*/createMediaMixin({
    min: breakpoints.tabletSmall,
    max: breakpoints.tabletLarge
  }),
  tabletLarge: /*#__PURE__*/createMediaMixin({
    max: breakpoints.laptop
  }),
  tabletLargeOnly: /*#__PURE__*/createMediaMixin({
    min: breakpoints.tabletLarge,
    max: breakpoints.laptop
  }),
  tablet: /*#__PURE__*/createMediaMixin({
    max: breakpoints.laptop
  }),
  tabletOnly: /*#__PURE__*/createMediaMixin({
    min: breakpoints.tabletSmall,
    max: breakpoints.laptop
  }),
  laptop: /*#__PURE__*/createMediaMixin({
    max: breakpoints.desktop
  }),
  laptopOnly: /*#__PURE__*/createMediaMixin({
    min: breakpoints.laptop,
    max: breakpoints.desktop
  }),
  desktopOnly: /*#__PURE__*/createMediaMixin({
    min: breakpoints.desktop
  })
};

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var runtime_1 = createCommonjsModule(function (module) {
/**
 * Copyright (c) 2014-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var runtime = (function (exports) {

  var Op = Object.prototype;
  var hasOwn = Op.hasOwnProperty;
  var undefined$1; // More compressible than void 0.
  var $Symbol = typeof Symbol === "function" ? Symbol : {};
  var iteratorSymbol = $Symbol.iterator || "@@iterator";
  var asyncIteratorSymbol = $Symbol.asyncIterator || "@@asyncIterator";
  var toStringTagSymbol = $Symbol.toStringTag || "@@toStringTag";

  function define(obj, key, value) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
    return obj[key];
  }
  try {
    // IE 8 has a broken Object.defineProperty that only works on DOM objects.
    define({}, "");
  } catch (err) {
    define = function(obj, key, value) {
      return obj[key] = value;
    };
  }

  function wrap(innerFn, outerFn, self, tryLocsList) {
    // If outerFn provided and outerFn.prototype is a Generator, then outerFn.prototype instanceof Generator.
    var protoGenerator = outerFn && outerFn.prototype instanceof Generator ? outerFn : Generator;
    var generator = Object.create(protoGenerator.prototype);
    var context = new Context(tryLocsList || []);

    // The ._invoke method unifies the implementations of the .next,
    // .throw, and .return methods.
    generator._invoke = makeInvokeMethod(innerFn, self, context);

    return generator;
  }
  exports.wrap = wrap;

  // Try/catch helper to minimize deoptimizations. Returns a completion
  // record like context.tryEntries[i].completion. This interface could
  // have been (and was previously) designed to take a closure to be
  // invoked without arguments, but in all the cases we care about we
  // already have an existing method we want to call, so there's no need
  // to create a new function object. We can even get away with assuming
  // the method takes exactly one argument, since that happens to be true
  // in every case, so we don't have to touch the arguments object. The
  // only additional allocation required is the completion record, which
  // has a stable shape and so hopefully should be cheap to allocate.
  function tryCatch(fn, obj, arg) {
    try {
      return { type: "normal", arg: fn.call(obj, arg) };
    } catch (err) {
      return { type: "throw", arg: err };
    }
  }

  var GenStateSuspendedStart = "suspendedStart";
  var GenStateSuspendedYield = "suspendedYield";
  var GenStateExecuting = "executing";
  var GenStateCompleted = "completed";

  // Returning this object from the innerFn has the same effect as
  // breaking out of the dispatch switch statement.
  var ContinueSentinel = {};

  // Dummy constructor functions that we use as the .constructor and
  // .constructor.prototype properties for functions that return Generator
  // objects. For full spec compliance, you may wish to configure your
  // minifier not to mangle the names of these two functions.
  function Generator() {}
  function GeneratorFunction() {}
  function GeneratorFunctionPrototype() {}

  // This is a polyfill for %IteratorPrototype% for environments that
  // don't natively support it.
  var IteratorPrototype = {};
  IteratorPrototype[iteratorSymbol] = function () {
    return this;
  };

  var getProto = Object.getPrototypeOf;
  var NativeIteratorPrototype = getProto && getProto(getProto(values([])));
  if (NativeIteratorPrototype &&
      NativeIteratorPrototype !== Op &&
      hasOwn.call(NativeIteratorPrototype, iteratorSymbol)) {
    // This environment has a native %IteratorPrototype%; use it instead
    // of the polyfill.
    IteratorPrototype = NativeIteratorPrototype;
  }

  var Gp = GeneratorFunctionPrototype.prototype =
    Generator.prototype = Object.create(IteratorPrototype);
  GeneratorFunction.prototype = Gp.constructor = GeneratorFunctionPrototype;
  GeneratorFunctionPrototype.constructor = GeneratorFunction;
  GeneratorFunction.displayName = define(
    GeneratorFunctionPrototype,
    toStringTagSymbol,
    "GeneratorFunction"
  );

  // Helper for defining the .next, .throw, and .return methods of the
  // Iterator interface in terms of a single ._invoke method.
  function defineIteratorMethods(prototype) {
    ["next", "throw", "return"].forEach(function(method) {
      define(prototype, method, function(arg) {
        return this._invoke(method, arg);
      });
    });
  }

  exports.isGeneratorFunction = function(genFun) {
    var ctor = typeof genFun === "function" && genFun.constructor;
    return ctor
      ? ctor === GeneratorFunction ||
        // For the native GeneratorFunction constructor, the best we can
        // do is to check its .name property.
        (ctor.displayName || ctor.name) === "GeneratorFunction"
      : false;
  };

  exports.mark = function(genFun) {
    if (Object.setPrototypeOf) {
      Object.setPrototypeOf(genFun, GeneratorFunctionPrototype);
    } else {
      genFun.__proto__ = GeneratorFunctionPrototype;
      define(genFun, toStringTagSymbol, "GeneratorFunction");
    }
    genFun.prototype = Object.create(Gp);
    return genFun;
  };

  // Within the body of any async function, `await x` is transformed to
  // `yield regeneratorRuntime.awrap(x)`, so that the runtime can test
  // `hasOwn.call(value, "__await")` to determine if the yielded value is
  // meant to be awaited.
  exports.awrap = function(arg) {
    return { __await: arg };
  };

  function AsyncIterator(generator, PromiseImpl) {
    function invoke(method, arg, resolve, reject) {
      var record = tryCatch(generator[method], generator, arg);
      if (record.type === "throw") {
        reject(record.arg);
      } else {
        var result = record.arg;
        var value = result.value;
        if (value &&
            typeof value === "object" &&
            hasOwn.call(value, "__await")) {
          return PromiseImpl.resolve(value.__await).then(function(value) {
            invoke("next", value, resolve, reject);
          }, function(err) {
            invoke("throw", err, resolve, reject);
          });
        }

        return PromiseImpl.resolve(value).then(function(unwrapped) {
          // When a yielded Promise is resolved, its final value becomes
          // the .value of the Promise<{value,done}> result for the
          // current iteration.
          result.value = unwrapped;
          resolve(result);
        }, function(error) {
          // If a rejected Promise was yielded, throw the rejection back
          // into the async generator function so it can be handled there.
          return invoke("throw", error, resolve, reject);
        });
      }
    }

    var previousPromise;

    function enqueue(method, arg) {
      function callInvokeWithMethodAndArg() {
        return new PromiseImpl(function(resolve, reject) {
          invoke(method, arg, resolve, reject);
        });
      }

      return previousPromise =
        // If enqueue has been called before, then we want to wait until
        // all previous Promises have been resolved before calling invoke,
        // so that results are always delivered in the correct order. If
        // enqueue has not been called before, then it is important to
        // call invoke immediately, without waiting on a callback to fire,
        // so that the async generator function has the opportunity to do
        // any necessary setup in a predictable way. This predictability
        // is why the Promise constructor synchronously invokes its
        // executor callback, and why async functions synchronously
        // execute code before the first await. Since we implement simple
        // async functions in terms of async generators, it is especially
        // important to get this right, even though it requires care.
        previousPromise ? previousPromise.then(
          callInvokeWithMethodAndArg,
          // Avoid propagating failures to Promises returned by later
          // invocations of the iterator.
          callInvokeWithMethodAndArg
        ) : callInvokeWithMethodAndArg();
    }

    // Define the unified helper method that is used to implement .next,
    // .throw, and .return (see defineIteratorMethods).
    this._invoke = enqueue;
  }

  defineIteratorMethods(AsyncIterator.prototype);
  AsyncIterator.prototype[asyncIteratorSymbol] = function () {
    return this;
  };
  exports.AsyncIterator = AsyncIterator;

  // Note that simple async functions are implemented on top of
  // AsyncIterator objects; they just return a Promise for the value of
  // the final result produced by the iterator.
  exports.async = function(innerFn, outerFn, self, tryLocsList, PromiseImpl) {
    if (PromiseImpl === void 0) PromiseImpl = Promise;

    var iter = new AsyncIterator(
      wrap(innerFn, outerFn, self, tryLocsList),
      PromiseImpl
    );

    return exports.isGeneratorFunction(outerFn)
      ? iter // If outerFn is a generator, return the full iterator.
      : iter.next().then(function(result) {
          return result.done ? result.value : iter.next();
        });
  };

  function makeInvokeMethod(innerFn, self, context) {
    var state = GenStateSuspendedStart;

    return function invoke(method, arg) {
      if (state === GenStateExecuting) {
        throw new Error("Generator is already running");
      }

      if (state === GenStateCompleted) {
        if (method === "throw") {
          throw arg;
        }

        // Be forgiving, per 25.3.3.3.3 of the spec:
        // https://people.mozilla.org/~jorendorff/es6-draft.html#sec-generatorresume
        return doneResult();
      }

      context.method = method;
      context.arg = arg;

      while (true) {
        var delegate = context.delegate;
        if (delegate) {
          var delegateResult = maybeInvokeDelegate(delegate, context);
          if (delegateResult) {
            if (delegateResult === ContinueSentinel) continue;
            return delegateResult;
          }
        }

        if (context.method === "next") {
          // Setting context._sent for legacy support of Babel's
          // function.sent implementation.
          context.sent = context._sent = context.arg;

        } else if (context.method === "throw") {
          if (state === GenStateSuspendedStart) {
            state = GenStateCompleted;
            throw context.arg;
          }

          context.dispatchException(context.arg);

        } else if (context.method === "return") {
          context.abrupt("return", context.arg);
        }

        state = GenStateExecuting;

        var record = tryCatch(innerFn, self, context);
        if (record.type === "normal") {
          // If an exception is thrown from innerFn, we leave state ===
          // GenStateExecuting and loop back for another invocation.
          state = context.done
            ? GenStateCompleted
            : GenStateSuspendedYield;

          if (record.arg === ContinueSentinel) {
            continue;
          }

          return {
            value: record.arg,
            done: context.done
          };

        } else if (record.type === "throw") {
          state = GenStateCompleted;
          // Dispatch the exception by looping back around to the
          // context.dispatchException(context.arg) call above.
          context.method = "throw";
          context.arg = record.arg;
        }
      }
    };
  }

  // Call delegate.iterator[context.method](context.arg) and handle the
  // result, either by returning a { value, done } result from the
  // delegate iterator, or by modifying context.method and context.arg,
  // setting context.delegate to null, and returning the ContinueSentinel.
  function maybeInvokeDelegate(delegate, context) {
    var method = delegate.iterator[context.method];
    if (method === undefined$1) {
      // A .throw or .return when the delegate iterator has no .throw
      // method always terminates the yield* loop.
      context.delegate = null;

      if (context.method === "throw") {
        // Note: ["return"] must be used for ES3 parsing compatibility.
        if (delegate.iterator["return"]) {
          // If the delegate iterator has a return method, give it a
          // chance to clean up.
          context.method = "return";
          context.arg = undefined$1;
          maybeInvokeDelegate(delegate, context);

          if (context.method === "throw") {
            // If maybeInvokeDelegate(context) changed context.method from
            // "return" to "throw", let that override the TypeError below.
            return ContinueSentinel;
          }
        }

        context.method = "throw";
        context.arg = new TypeError(
          "The iterator does not provide a 'throw' method");
      }

      return ContinueSentinel;
    }

    var record = tryCatch(method, delegate.iterator, context.arg);

    if (record.type === "throw") {
      context.method = "throw";
      context.arg = record.arg;
      context.delegate = null;
      return ContinueSentinel;
    }

    var info = record.arg;

    if (! info) {
      context.method = "throw";
      context.arg = new TypeError("iterator result is not an object");
      context.delegate = null;
      return ContinueSentinel;
    }

    if (info.done) {
      // Assign the result of the finished delegate to the temporary
      // variable specified by delegate.resultName (see delegateYield).
      context[delegate.resultName] = info.value;

      // Resume execution at the desired location (see delegateYield).
      context.next = delegate.nextLoc;

      // If context.method was "throw" but the delegate handled the
      // exception, let the outer generator proceed normally. If
      // context.method was "next", forget context.arg since it has been
      // "consumed" by the delegate iterator. If context.method was
      // "return", allow the original .return call to continue in the
      // outer generator.
      if (context.method !== "return") {
        context.method = "next";
        context.arg = undefined$1;
      }

    } else {
      // Re-yield the result returned by the delegate method.
      return info;
    }

    // The delegate iterator is finished, so forget it and continue with
    // the outer generator.
    context.delegate = null;
    return ContinueSentinel;
  }

  // Define Generator.prototype.{next,throw,return} in terms of the
  // unified ._invoke helper method.
  defineIteratorMethods(Gp);

  define(Gp, toStringTagSymbol, "Generator");

  // A Generator should always return itself as the iterator object when the
  // @@iterator function is called on it. Some browsers' implementations of the
  // iterator prototype chain incorrectly implement this, causing the Generator
  // object to not be returned from this call. This ensures that doesn't happen.
  // See https://github.com/facebook/regenerator/issues/274 for more details.
  Gp[iteratorSymbol] = function() {
    return this;
  };

  Gp.toString = function() {
    return "[object Generator]";
  };

  function pushTryEntry(locs) {
    var entry = { tryLoc: locs[0] };

    if (1 in locs) {
      entry.catchLoc = locs[1];
    }

    if (2 in locs) {
      entry.finallyLoc = locs[2];
      entry.afterLoc = locs[3];
    }

    this.tryEntries.push(entry);
  }

  function resetTryEntry(entry) {
    var record = entry.completion || {};
    record.type = "normal";
    delete record.arg;
    entry.completion = record;
  }

  function Context(tryLocsList) {
    // The root entry object (effectively a try statement without a catch
    // or a finally block) gives us a place to store values thrown from
    // locations where there is no enclosing try statement.
    this.tryEntries = [{ tryLoc: "root" }];
    tryLocsList.forEach(pushTryEntry, this);
    this.reset(true);
  }

  exports.keys = function(object) {
    var keys = [];
    for (var key in object) {
      keys.push(key);
    }
    keys.reverse();

    // Rather than returning an object with a next method, we keep
    // things simple and return the next function itself.
    return function next() {
      while (keys.length) {
        var key = keys.pop();
        if (key in object) {
          next.value = key;
          next.done = false;
          return next;
        }
      }

      // To avoid creating an additional object, we just hang the .value
      // and .done properties off the next function object itself. This
      // also ensures that the minifier will not anonymize the function.
      next.done = true;
      return next;
    };
  };

  function values(iterable) {
    if (iterable) {
      var iteratorMethod = iterable[iteratorSymbol];
      if (iteratorMethod) {
        return iteratorMethod.call(iterable);
      }

      if (typeof iterable.next === "function") {
        return iterable;
      }

      if (!isNaN(iterable.length)) {
        var i = -1, next = function next() {
          while (++i < iterable.length) {
            if (hasOwn.call(iterable, i)) {
              next.value = iterable[i];
              next.done = false;
              return next;
            }
          }

          next.value = undefined$1;
          next.done = true;

          return next;
        };

        return next.next = next;
      }
    }

    // Return an iterator with no values.
    return { next: doneResult };
  }
  exports.values = values;

  function doneResult() {
    return { value: undefined$1, done: true };
  }

  Context.prototype = {
    constructor: Context,

    reset: function(skipTempReset) {
      this.prev = 0;
      this.next = 0;
      // Resetting context._sent for legacy support of Babel's
      // function.sent implementation.
      this.sent = this._sent = undefined$1;
      this.done = false;
      this.delegate = null;

      this.method = "next";
      this.arg = undefined$1;

      this.tryEntries.forEach(resetTryEntry);

      if (!skipTempReset) {
        for (var name in this) {
          // Not sure about the optimal order of these conditions:
          if (name.charAt(0) === "t" &&
              hasOwn.call(this, name) &&
              !isNaN(+name.slice(1))) {
            this[name] = undefined$1;
          }
        }
      }
    },

    stop: function() {
      this.done = true;

      var rootEntry = this.tryEntries[0];
      var rootRecord = rootEntry.completion;
      if (rootRecord.type === "throw") {
        throw rootRecord.arg;
      }

      return this.rval;
    },

    dispatchException: function(exception) {
      if (this.done) {
        throw exception;
      }

      var context = this;
      function handle(loc, caught) {
        record.type = "throw";
        record.arg = exception;
        context.next = loc;

        if (caught) {
          // If the dispatched exception was caught by a catch block,
          // then let that catch block handle the exception normally.
          context.method = "next";
          context.arg = undefined$1;
        }

        return !! caught;
      }

      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        var record = entry.completion;

        if (entry.tryLoc === "root") {
          // Exception thrown outside of any try block that could handle
          // it, so set the completion value of the entire function to
          // throw the exception.
          return handle("end");
        }

        if (entry.tryLoc <= this.prev) {
          var hasCatch = hasOwn.call(entry, "catchLoc");
          var hasFinally = hasOwn.call(entry, "finallyLoc");

          if (hasCatch && hasFinally) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            } else if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }

          } else if (hasCatch) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            }

          } else if (hasFinally) {
            if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }

          } else {
            throw new Error("try statement without catch or finally");
          }
        }
      }
    },

    abrupt: function(type, arg) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc <= this.prev &&
            hasOwn.call(entry, "finallyLoc") &&
            this.prev < entry.finallyLoc) {
          var finallyEntry = entry;
          break;
        }
      }

      if (finallyEntry &&
          (type === "break" ||
           type === "continue") &&
          finallyEntry.tryLoc <= arg &&
          arg <= finallyEntry.finallyLoc) {
        // Ignore the finally entry if control is not jumping to a
        // location outside the try/catch block.
        finallyEntry = null;
      }

      var record = finallyEntry ? finallyEntry.completion : {};
      record.type = type;
      record.arg = arg;

      if (finallyEntry) {
        this.method = "next";
        this.next = finallyEntry.finallyLoc;
        return ContinueSentinel;
      }

      return this.complete(record);
    },

    complete: function(record, afterLoc) {
      if (record.type === "throw") {
        throw record.arg;
      }

      if (record.type === "break" ||
          record.type === "continue") {
        this.next = record.arg;
      } else if (record.type === "return") {
        this.rval = this.arg = record.arg;
        this.method = "return";
        this.next = "end";
      } else if (record.type === "normal" && afterLoc) {
        this.next = afterLoc;
      }

      return ContinueSentinel;
    },

    finish: function(finallyLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.finallyLoc === finallyLoc) {
          this.complete(entry.completion, entry.afterLoc);
          resetTryEntry(entry);
          return ContinueSentinel;
        }
      }
    },

    "catch": function(tryLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc === tryLoc) {
          var record = entry.completion;
          if (record.type === "throw") {
            var thrown = record.arg;
            resetTryEntry(entry);
          }
          return thrown;
        }
      }

      // The context.catch method must only be called with a location
      // argument that corresponds to a known catch block.
      throw new Error("illegal catch attempt");
    },

    delegateYield: function(iterable, resultName, nextLoc) {
      this.delegate = {
        iterator: values(iterable),
        resultName: resultName,
        nextLoc: nextLoc
      };

      if (this.method === "next") {
        // Deliberately forget the last sent value so that we don't
        // accidentally pass it on to the delegate.
        this.arg = undefined$1;
      }

      return ContinueSentinel;
    }
  };

  // Regardless of whether this script is executing as a CommonJS module
  // or not, return the runtime object so that we can declare the variable
  // regeneratorRuntime in the outer scope, which allows this module to be
  // injected easily by `bin/regenerator --include-runtime script.js`.
  return exports;

}(
  // If this script is executing as a CommonJS module, use module.exports
  // as the regeneratorRuntime namespace. Otherwise create a new empty
  // object. Either way, the resulting object will be used to initialize
  // the regeneratorRuntime variable at the top of this file.
   module.exports 
));

try {
  regeneratorRuntime = runtime;
} catch (accidentalStrictMode) {
  // This module should not be running in strict mode, so the above
  // assignment should always work unless something is misconfigured. Just
  // in case runtime.js accidentally runs in strict mode, we can escape
  // strict mode using a global Function call. This could conceivably fail
  // if a Content Security Policy forbids using Function, but in that case
  // the proper solution is to fix the accidental strict mode problem. If
  // you've misconfigured your bundler to force strict mode and applied a
  // CSP to forbid Function, and you're not willing to fix either of those
  // problems, please detail your unique predicament in a GitHub issue.
  Function("r", "regeneratorRuntime = r")(runtime);
}
});

function getErrorDetails(_x) {
  return _getErrorDetails.apply(this, arguments);
}

function _getErrorDetails() {
  _getErrorDetails = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(id) {
    var _yield$getIssueById;

    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return getIssueById(id);

          case 2:
            _context.t0 = _yield$getIssueById = _context.sent;

            if (!(_context.t0 == null)) {
              _context.next = 7;
              break;
            }

            _context.t1 = void 0;
            _context.next = 8;
            break;

          case 7:
            _context.t1 = _yield$getIssueById.data;

          case 8:
            return _context.abrupt("return", _context.t1);

          case 9:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _getErrorDetails.apply(this, arguments);
}

function getIssueById(id) {
  return request.get({
    path: "/tager/sentry-issue/" + id
  });
}

function getFailureMessage(code, message) {
  if (code === 404) return 'Tager Backend Sentry module is not installed';else return message;
}

function _extends$4() { _extends$4 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4.apply(this, arguments); }

function _objectWithoutProperties$3(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$4(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$4(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

var _ref2$3 = /*#__PURE__*/createElement("path", {
  d: "M309.974 506.617C306.99 506.617 303.047 505.125 298.039 502.141L26.6316 317.687C19.1724 312.785 13.7379 307.777 10.2214 302.768C6.70498 297.867 5 291.899 5 284.866V261.103C5 254.177 6.59839 248.423 9.79518 243.947C12.992 239.472 18.6396 234.57 26.525 229.029L298.039 44.5744C302.941 41.5907 306.99 40.0989 309.974 40.0989C314.449 40.0989 318.179 41.4842 321.162 44.1482C324.146 46.9187 325.638 50.7548 325.638 55.6566V139.839C325.638 147.298 323.826 153.159 320.097 157.314C316.367 161.577 311.252 165.626 304.859 169.569L146.405 272.932L304.859 377.04C311.785 382.049 317.006 386.311 320.523 390.041C324.039 393.77 325.744 399.098 325.744 406.024V490.846C325.744 495.321 324.252 499.051 321.269 502.035C318.179 505.125 314.449 506.617 309.974 506.617Z",
  fill: "black"
});

var _ref3$3 = /*#__PURE__*/createElement("path", {
  d: "M528.421 263.448C530.766 263.448 533.004 263.448 535.348 263.448C539.61 263.448 543.766 263.448 548.028 263.448C548.135 241.816 548.348 224.98 548.774 212.832C549.733 180.651 557.938 156.675 573.283 141.011C588.628 125.347 609.513 117.568 635.727 117.568C661.514 117.568 682.08 125.347 697.425 141.011C712.769 156.675 720.975 180.545 721.934 212.832C722.68 234.251 723.212 251.194 723.319 263.448C771.377 263.554 819.542 263.768 867.601 264.3C867.387 250.661 866.961 232.759 866.215 210.594C864.191 145.167 842.559 94.6576 801.107 59.1732C759.656 23.6889 704.564 6 635.62 6C566.144 6 510.733 23.6889 469.387 59.1732C427.936 94.6576 406.304 145.06 404.28 210.594C403.853 223.062 403.64 240.964 403.534 264.3C445.305 263.768 486.863 263.554 528.421 263.448Z",
  fill: "black"
});

var _ref4 = /*#__PURE__*/createElement("path", {
  d: "M723.108 303.41C722.895 312.467 722.576 322.91 722.149 334.738C721.19 367.026 712.985 390.895 697.641 406.56C682.296 422.224 661.73 430.003 635.943 430.003C609.622 430.003 588.843 422.224 573.499 406.56C558.154 390.895 549.949 367.026 548.99 334.738C548.67 326.427 548.457 315.984 548.351 303.516C495.284 303.41 444.988 303.09 403.962 302.664C404.069 318.009 404.282 330.156 404.602 339.214C407.053 405.707 428.684 456.003 469.283 490.209C509.989 524.415 565.507 541.571 635.943 541.571C706.378 541.571 761.789 524.415 802.176 490.209C842.562 456.003 864.087 405.707 866.538 339.214C867.07 325.361 867.39 313.107 867.603 302.451C828.922 302.877 778.2 303.303 723.108 303.41Z",
  fill: "#DD6900"
});

var _ref5 = /*#__PURE__*/createElement("path", {
  d: "M939.311 410.61C980.017 362.019 1016.67 318.116 1049.39 278.902C1068.78 255.14 1084.34 236.279 1096.27 222.319C1108.21 208.467 1119.18 195.253 1129.41 182.892C1139.54 170.531 1148.38 159.982 1155.84 151.244C1163.3 142.613 1167.77 137.285 1169.27 135.26C1170.76 133.236 1171.08 131.531 1170.33 130.039C1169.59 128.547 1168.2 127.801 1166.28 127.801H954.229C948.795 127.801 944.319 126.096 940.803 122.58C937.286 119.063 935.581 114.694 935.581 109.153V77.9311C935.581 69.5128 937.286 61.4143 940.803 53.742C944.319 46.0697 948.901 39.2499 954.549 33.2826C960.197 27.3152 967.016 22.5201 975.008 18.7905C982.894 15.0609 991.419 13.2494 1000.26 13.2494H1331.34C1337.31 13.2494 1341.89 15.0609 1345.09 18.7905C1348.29 22.5201 1349.89 27.1021 1349.89 32.5367V106.915C1349.89 116.293 1346.9 124.817 1340.93 132.17C1300.76 181.72 1264.85 226.688 1233.1 266.861C1213.7 290.624 1198.36 309.592 1186.96 323.764C1175.55 337.936 1165 351.043 1155.31 363.191C1145.61 375.339 1137.3 385.675 1130.37 394.093C1123.45 402.512 1119.5 407.52 1118.44 409.012C1117.48 410.504 1117.48 411.995 1118.44 413.487C1119.4 414.979 1120.46 415.725 1121.42 415.725H1340.08C1345.52 415.725 1349.99 417.43 1353.51 420.946C1357.02 424.463 1358.73 428.832 1358.73 434.373V513.227C1358.73 518.662 1357.02 523.137 1353.51 526.654C1349.99 530.17 1345.62 531.875 1340.08 531.875H947.303C941.868 531.875 937.286 530.063 933.557 526.334C929.827 522.604 928.016 518.022 928.016 512.588V436.717C928.016 429.791 928.975 425.102 930.999 422.545C931.958 420.52 933.131 418.709 934.303 417.004C935.688 415.192 937.286 413.168 939.311 410.61Z",
  fill: "black"
});

var _ref6 = /*#__PURE__*/createElement("path", {
  d: "M1436.2 515.572V80.383C1436.2 61.5219 1442.6 46.0708 1455.6 33.923C1468.49 21.7752 1483.62 15.7013 1500.99 15.7013H1560.45C1566.42 15.7013 1570.89 17.4062 1573.88 20.9227C1577.29 24.4392 1579.1 28.9147 1579.1 34.3492V515.679C1579.1 521.114 1577.39 525.589 1573.88 529.105C1570.89 532.622 1566.42 534.327 1560.45 534.327H1454.85C1449.42 534.327 1444.94 532.622 1441.42 529.105C1438.01 525.482 1436.2 521.007 1436.2 515.572Z",
  fill: "black"
});

var _ref7 = /*#__PURE__*/createElement("path", {
  d: "M1689.92 506.622C1685.44 506.622 1681.71 505.13 1678.73 502.146C1675.75 499.162 1674.26 495.433 1674.26 490.957V406.136C1674.26 399.209 1675.96 393.881 1679.48 390.152C1682.89 386.422 1688.11 382.053 1695.14 377.152L1853.6 273.043L1695.14 169.68C1688.64 165.737 1683.63 161.581 1679.9 157.426C1676.17 153.27 1674.36 147.409 1674.36 139.95V55.8742C1674.36 50.9725 1675.85 47.0298 1678.84 44.3658C1681.82 41.5952 1685.55 40.3165 1690.03 40.3165C1693.01 40.3165 1696.95 41.8083 1701.96 44.792L1973.48 229.247C1981.36 234.681 1987.01 239.69 1990.2 244.165C1993.4 248.641 1995 254.288 1995 261.321V285.084C1995 292.01 1993.3 297.978 1989.78 302.986C1986.26 307.994 1980.83 312.896 1973.37 317.904L1701.75 502.146C1696.85 505.13 1692.9 506.622 1689.92 506.622Z",
  fill: "black"
});

var _ref8 = /*#__PURE__*/createElement("path", {
  d: "M558.042 638.324V656.546H543.55C535.877 678.71 528.312 700.768 520.959 722.826C513.5 744.884 506.041 766.941 498.582 789.106H472.688C468.532 775.892 464.482 762.892 460.327 749.892C456.171 736.892 452.122 723.891 447.966 710.678L421.219 789.106H395.325L353.661 656.546H338.529V638.324H372.948L408.219 757.138C412.801 742.966 417.383 728.9 422.072 714.834C426.76 700.768 431.449 686.702 436.138 672.53L425.695 638.324H450.31L485.581 757.138L524.476 638.324H558.042V638.324Z",
  fill: "#DD6900"
});

var _ref9 = /*#__PURE__*/createElement("path", {
  d: "M612.064 791.557C605.777 791.557 599.49 790.704 593.097 789.106C586.597 787.508 580.736 784.524 575.408 780.368C570.08 776.212 565.711 770.565 562.301 763.532C558.891 756.499 557.186 747.654 557.186 736.892V719.842C557.186 709.08 558.785 700.448 562.088 693.948C565.391 687.448 569.547 682.44 574.769 678.817C579.99 675.3 585.638 672.956 592.031 671.89C598.318 670.825 604.499 670.292 610.36 670.292C616.114 670.292 621.974 670.931 627.729 672.21C633.483 673.489 638.598 675.833 643.18 679.35C647.762 682.866 651.385 687.661 654.156 693.842C656.926 700.022 658.311 708.014 658.311 717.924C658.311 721.334 658.205 724.744 657.992 728.367C657.779 731.883 657.352 734.867 656.82 737.318H579.884C579.884 744.031 580.949 749.572 582.974 754.154C584.998 758.736 587.662 762.253 590.753 765.023C593.843 767.687 597.253 769.605 600.876 770.778C604.499 771.95 607.802 772.483 610.999 772.483C619.63 772.483 626.77 772.163 632.311 771.417C637.958 770.671 643.073 769.819 647.762 768.86L653.942 784.417C648.721 786.868 642.434 788.68 635.188 789.852C627.622 790.917 619.95 791.557 612.064 791.557ZM609.081 687.768C605.564 687.768 602.048 688.087 598.638 688.727C595.228 689.366 592.138 690.858 589.474 693.202C586.81 695.547 584.572 698.956 582.867 703.432C581.162 707.908 580.203 713.875 580.097 721.441H634.122C634.868 720.482 635.294 719.416 635.401 718.244C635.508 717.072 635.614 715.58 635.614 713.555C635.508 708.867 634.655 704.924 633.057 701.62C631.458 698.317 629.54 695.76 627.089 693.628C624.638 691.604 621.868 690.112 618.778 689.153C615.688 688.301 612.491 687.768 609.081 687.768Z",
  fill: "#DD6900"
});

var _ref10 = /*#__PURE__*/createElement("path", {
  d: "M712.342 625.536V677.857C714.047 676.259 717.244 674.66 722.039 672.955C726.834 671.25 733.335 670.398 741.646 670.398C747.507 670.398 753.155 671.144 758.909 672.636C764.557 674.127 769.671 676.791 774.147 680.734C778.622 684.677 782.139 690.005 784.909 696.825C787.573 703.644 788.959 712.276 788.959 722.719V740.194C788.959 750.957 787.573 759.695 784.803 766.408C782.032 773.121 778.409 778.343 773.827 782.072C769.245 785.802 764.237 788.253 758.483 789.638C752.835 791.023 747.081 791.663 741.22 791.663C732.269 791.663 725.343 791.023 720.334 789.638C715.326 788.253 710.85 786.654 707.014 784.843C706.055 787.72 704.777 789.851 703.285 791.45C701.793 793.048 700.301 794.113 698.916 794.859L684.424 789.958C685.17 788.572 686.022 785.589 687.088 781.22C688.153 776.851 688.686 770.67 688.686 762.785V643.545H673.128V625.643H712.342V625.536ZM712.342 753.514C712.342 758.523 712.129 762.892 711.703 766.941C713.088 767.687 714.687 768.433 716.285 769.179C717.883 769.924 719.695 770.67 721.72 771.31C723.744 771.949 725.982 772.482 728.646 772.802C731.31 773.121 734.4 773.334 737.917 773.334C741.859 773.334 745.589 772.908 748.999 771.949C752.409 770.99 755.286 769.179 757.843 766.515C760.294 763.851 762.319 760.015 763.704 755.006C765.089 749.998 765.835 743.604 765.835 735.719V726.768C765.835 718.989 764.983 712.595 763.384 707.8C761.786 702.898 759.655 699.062 756.991 696.292C754.327 693.521 751.343 691.71 747.827 690.751C744.31 689.792 740.794 689.365 737.064 689.365C732.056 689.365 727.474 690.005 723.105 691.283C718.736 692.562 715.219 694.161 712.342 695.972V753.514V753.514Z",
  fill: "#DD6900"
});

var _ref11 = /*#__PURE__*/createElement("path", {
  d: "M870.048 656.546V638.324H978.846V667.628H960.198V656.546H909.369V703.325H958.28V721.973H909.369V770.884H962.116V759.802H980.764V789.106H874.95V770.884H884.754V656.546H870.048V656.546Z",
  fill: "#DD6900"
});

var _ref12 = /*#__PURE__*/createElement("path", {
  d: "M1118.01 672.318V690.646C1115.88 690.646 1113.64 690.859 1111.41 691.179C1109.17 691.499 1106.61 692.458 1103.95 694.056L1096.91 702.368C1092.76 707.163 1088.82 711.958 1084.87 716.753C1080.93 721.548 1076.99 726.237 1072.83 730.926L1106.82 771.099H1117.91V789.32H1091.8L1057.81 748.721L1024.03 789.32H998.347V771.099C1001.22 771.099 1003.67 770.779 1005.7 770.246C1007.72 769.713 1010.17 768.435 1012.84 766.41L1042.78 730.926L1009 690.753H998.347V672.425H1023.81C1029.68 679.244 1035.32 686.064 1040.86 692.884C1046.41 699.704 1052.05 706.63 1057.91 713.343L1092.12 672.318H1118.01V672.318Z",
  fill: "#DD6900"
});

var _ref13 = /*#__PURE__*/createElement("path", {
  d: "M1170.65 826.823C1166.81 828.102 1162.98 829.381 1158.93 830.553C1154.99 831.725 1151.04 833.004 1147.21 834.282V700.657C1147.21 696.394 1146.67 693.411 1145.5 691.706C1144.33 690.001 1142.62 689.148 1140.17 689.148C1137.19 689.148 1134.31 689.681 1131.65 690.64L1129.73 676.361C1132.71 675.296 1135.48 674.23 1137.94 673.377C1140.39 672.525 1143.05 672.099 1145.93 672.099C1149.87 672.099 1153.17 672.738 1155.84 674.017C1158.5 675.296 1160.74 677.427 1162.76 680.304C1166.71 677.64 1171.61 675.295 1177.36 673.377C1183.12 671.459 1190.58 670.394 1199.74 670.394C1205.6 670.394 1211.36 671.14 1217.11 672.525C1222.86 673.91 1227.98 676.681 1232.45 680.623C1236.93 684.566 1240.55 690.001 1243.32 696.821C1246.09 703.64 1247.48 712.591 1247.48 723.46V741.256C1247.48 751.699 1246.09 760.224 1243.32 766.724C1240.55 773.224 1236.93 778.339 1232.45 781.962C1227.98 785.691 1222.97 788.142 1217.32 789.527C1211.68 790.913 1206.03 791.552 1200.17 791.552C1191.85 791.552 1185.35 791.019 1180.88 789.847C1176.3 788.675 1172.99 787.396 1170.65 785.798V826.823V826.823ZM1170.65 767.47C1173.53 768.748 1176.62 769.92 1180.24 770.879C1183.76 771.839 1188.87 772.371 1195.58 772.371C1199.42 772.371 1203.04 771.945 1206.56 771.093C1210.08 770.24 1213.06 768.535 1215.72 765.978C1218.39 763.42 1220.41 759.797 1222.01 755.002C1223.61 750.207 1224.36 743.92 1224.36 736.035V727.083C1224.36 719.198 1223.61 712.698 1222.12 707.796C1220.63 702.788 1218.6 698.952 1216.04 696.075C1213.49 693.198 1210.5 691.279 1207.2 690.214C1203.9 689.148 1200.27 688.615 1196.44 688.615C1191 688.615 1186.1 689.468 1181.63 691.066C1177.15 692.665 1173.53 694.476 1170.65 696.501V767.47V767.47Z",
  fill: "#DD6900"
});

var _ref14 = /*#__PURE__*/createElement("path", {
  d: "M1323.04 791.557C1316.76 791.557 1310.47 790.704 1304.07 789.106C1297.57 787.508 1291.71 784.524 1286.39 780.368C1281.06 776.212 1276.69 770.565 1273.28 763.532C1269.87 756.499 1268.16 747.654 1268.16 736.892V719.842C1268.16 709.08 1269.76 700.448 1273.07 693.948C1276.37 687.448 1280.53 682.44 1285.75 678.817C1290.97 675.3 1296.62 672.956 1303.01 671.89C1309.3 670.825 1315.48 670.292 1321.34 670.292C1327.2 670.292 1332.95 670.931 1338.71 672.21C1344.46 673.489 1349.58 675.833 1354.16 679.35C1358.74 682.866 1362.36 687.661 1365.13 693.842C1367.9 700.022 1369.29 708.014 1369.29 717.924C1369.29 721.334 1369.18 724.744 1368.97 728.367C1368.76 731.883 1368.33 734.867 1367.8 737.318H1290.86C1290.86 744.031 1291.93 749.572 1293.95 754.154C1295.98 758.736 1298.64 762.253 1301.73 765.023C1304.82 767.687 1308.23 769.605 1311.85 770.778C1315.48 771.95 1318.78 772.483 1321.98 772.483C1330.61 772.483 1337.75 772.163 1343.29 771.417C1348.94 770.671 1354.05 769.819 1358.74 768.86L1364.92 784.417C1359.7 786.868 1353.41 788.68 1346.17 789.852C1338.6 790.917 1331.03 791.557 1323.04 791.557ZM1320.06 687.768C1316.54 687.768 1313.03 688.087 1309.62 688.727C1306.21 689.366 1303.12 690.858 1300.45 693.202C1297.79 695.547 1295.55 698.956 1293.85 703.432C1292.14 707.908 1291.18 713.875 1291.07 721.441H1345.1C1345.85 720.482 1346.27 719.416 1346.38 718.244C1346.49 717.072 1346.59 715.58 1346.59 713.555C1346.49 708.867 1345.63 704.924 1344.03 701.62C1342.44 698.317 1340.52 695.76 1338.07 693.628C1335.62 691.604 1332.85 690.112 1329.76 689.153C1326.77 688.301 1323.47 687.768 1320.06 687.768Z",
  fill: "#DD6900"
});

var _ref15 = /*#__PURE__*/createElement("path", {
  d: "M1460.28 698.419L1443.87 699.698L1442.81 690.96C1440.14 690.96 1437.16 691.173 1433.86 691.493C1430.55 691.812 1427.57 692.558 1424.69 693.624C1421.82 694.689 1419.47 696.181 1417.55 698.099C1415.64 700.017 1414.68 702.575 1414.68 705.665V789.208H1391.13V694.902L1377.06 695.968L1376.42 679.345C1380.68 677.746 1385.27 676.468 1390.17 675.509C1395.07 674.55 1400.08 673.697 1405.41 672.951V684.033C1409.14 679.878 1413.4 676.681 1418.51 674.123C1423.52 671.673 1430.02 670.394 1437.91 670.394C1441.32 670.394 1444.83 670.607 1448.56 670.927C1452.29 671.246 1456.13 671.886 1460.28 672.738V698.419Z",
  fill: "#DD6900"
});

var _ref16 = /*#__PURE__*/createElement("path", {
  d: "M1512.61 635.978V671.675H1537.22V690.536H1512.4V761.505C1512.4 768.857 1514.85 772.587 1519.64 772.587C1521.77 772.587 1524.01 772.374 1526.46 771.948C1528.91 771.521 1531.9 770.989 1535.63 770.243L1537.97 787.079C1534.67 787.825 1530.94 788.571 1526.57 789.53C1522.2 790.489 1517.94 790.915 1513.46 790.915C1504.94 790.915 1498.76 787.612 1494.92 780.899C1490.98 774.292 1489.06 765.234 1489.06 753.939V690.643H1470.2L1471.9 671.782H1486.82C1488.1 668.904 1489.17 664.962 1490.02 659.847C1490.87 654.732 1491.3 647.486 1491.3 638.215L1512.61 635.978Z",
  fill: "#DD6900"
});

var _ref17 = /*#__PURE__*/createElement("path", {
  d: "M1628.86 703.961V691.813C1625.98 690.641 1622.89 689.789 1619.38 689.043C1615.86 688.297 1612.35 687.977 1608.62 687.977C1600.41 687.977 1594.02 689.149 1589.54 691.6C1584.96 694.051 1582.72 697.248 1582.72 701.404C1582.72 705.986 1584.43 709.396 1587.73 711.74C1591.03 714.084 1596.57 715.896 1604.25 717.174C1608.51 718.347 1613.52 719.306 1619.38 720.158C1625.24 721.011 1630.67 722.502 1636 724.634C1641.22 726.765 1645.7 730.068 1649.43 734.437C1653.16 738.806 1654.97 745.093 1654.97 753.298C1654.97 766.192 1650.81 775.782 1642.5 782.069C1634.19 788.356 1622.47 791.553 1607.34 791.553C1598.17 791.553 1589.54 791.02 1581.76 790.061C1573.88 789.102 1566.52 787.61 1559.6 785.799L1561.73 755.003H1579.84V767.151C1582.51 768.323 1585.92 769.389 1590.07 770.454C1594.23 771.52 1598.92 772.053 1604.35 772.053C1610.75 772.053 1615.76 771.307 1619.38 769.708C1623 768.11 1625.67 766.298 1627.48 764.274C1629.29 762.249 1630.35 760.118 1630.67 758.093C1630.99 756.069 1631.21 754.47 1631.21 753.511C1631.21 750.847 1630.46 748.503 1628.86 746.691C1627.26 744.88 1625.03 743.282 1622.15 742.109C1619.27 740.937 1615.76 739.872 1611.49 739.019C1607.34 738.167 1602.65 737.314 1597.32 736.462C1584.32 734.224 1574.84 730.494 1569.08 725.38C1563.22 720.265 1560.34 713.019 1560.34 703.535C1560.34 697.035 1561.52 691.6 1563.75 687.231C1565.99 682.862 1569.3 679.452 1573.45 676.682C1577.61 674.018 1582.72 672.1 1588.69 670.928C1594.66 669.755 1601.37 669.223 1608.83 669.223C1614.26 669.223 1619.91 669.649 1626.09 670.395C1632.17 671.141 1639.41 672.526 1647.72 674.551L1645.81 703.855H1628.86V703.961Z",
  fill: "#DD6900"
});

function SvgOzitagLogo(_ref, svgRef) {
  var title = _ref.title,
      titleId = _ref.titleId,
      props = _objectWithoutProperties$3(_ref, ["title", "titleId"]);

  return /*#__PURE__*/createElement("svg", _extends$4({
    viewBox: "0 0 2000 840",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ref: svgRef,
    "aria-labelledby": titleId
  }, props), title ? /*#__PURE__*/createElement("title", {
    id: titleId
  }, title) : null, _ref2$3, _ref3$3, _ref4, _ref5, _ref6, _ref7, _ref8, _ref9, _ref10, _ref11, _ref12, _ref13, _ref14, _ref15, _ref16, _ref17);
}

var ForwardRef$3 = /*#__PURE__*/forwardRef(SvgOzitagLogo);

function _templateObject4$4() {
  var data = _taggedTemplateLiteralLoose(["\n  display: inline-block;\n  height: 36px;\n\n  svg {\n    height: 36px;\n  }\n"]);

  _templateObject4$4 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$5() {
  var data = _taggedTemplateLiteralLoose(["\n  display: flex;\n  align-items: center;\n  font-size: 15px;\n  line-height: 22px;\n\n  span {\n    font-weight: 700;\n    display: inline-block;\n    margin-left: 6px;\n    letter-spacing: 0.2em;\n    font-size: 18px;\n  }\n"]);

  _templateObject3$5 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$5() {
  var data = _taggedTemplateLiteralLoose(["\n    padding: 15px;\n  "]);

  _templateObject2$5 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$8() {
  var data = _taggedTemplateLiteralLoose(["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  flex-shrink: 0;\n  padding: 15px 20px;\n  border-top: 1px solid rgb(230, 230, 230);\n\n  ", "\n"]);

  _templateObject$8 = function _templateObject() {
    return data;
  };

  return data;
}

function ErrorCardFooter() {
  return React__default.createElement(ErrorFooter, null, React__default.createElement(FooterTager, {
    href: "https://ozitag.com/tager",
    target: "_blank"
  }, "Made by ", React__default.createElement("span", null, "TAGER")), React__default.createElement(FooterOZiTAG, {
    href: "https://ozitag.com/",
    target: "_blank"
  }, React__default.createElement(ForwardRef$3, null)));
}

var ErrorFooter = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject$8(), /*#__PURE__*/media.mobile( /*#__PURE__*/css( /*#__PURE__*/_templateObject2$5())));
var FooterTager = /*#__PURE__*/styled.a( /*#__PURE__*/_templateObject3$5());
var FooterOZiTAG = /*#__PURE__*/styled.a( /*#__PURE__*/_templateObject4$4());

function _templateObject31() {
  var data = _taggedTemplateLiteralLoose(["\n  white-space: nowrap;\n  background: ", ";\n"]);

  _templateObject31 = function _templateObject31() {
    return data;
  };

  return data;
}

function _templateObject30() {
  var data = _taggedTemplateLiteralLoose(["\n  display: inline-block;\n  cursor: pointer;\n\n  &:not(:hover) {\n    text-decoration: underline;\n  }\n"]);

  _templateObject30 = function _templateObject30() {
    return data;
  };

  return data;
}

function _templateObject29() {
  var data = _taggedTemplateLiteralLoose(["\n  display: block;\n  padding: 5px 0 5px 15px;\n  word-break: break-word;\n  color: #999999;\n  overflow: auto;\n"]);

  _templateObject29 = function _templateObject29() {
    return data;
  };

  return data;
}

function _templateObject28() {
  var data = _taggedTemplateLiteralLoose(["\n  display: block;\n  background: #fff;\n  font-size: 16px;\n  margin-bottom: 5px;\n  font-weight: 700;\n"]);

  _templateObject28 = function _templateObject28() {
    return data;
  };

  return data;
}

function _templateObject27() {
  var data = _taggedTemplateLiteralLoose(["\n  font-weight: 700;\n"]);

  _templateObject27 = function _templateObject27() {
    return data;
  };

  return data;
}

function _templateObject26() {
  var data = _taggedTemplateLiteralLoose(["\n  word-break: break-word;\n"]);

  _templateObject26 = function _templateObject26() {
    return data;
  };

  return data;
}

function _templateObject25() {
  var data = _taggedTemplateLiteralLoose(["\n      ", ";\n      min-height: 48px;\n    "]);

  _templateObject25 = function _templateObject25() {
    return data;
  };

  return data;
}

function _templateObject24() {
  var data = _taggedTemplateLiteralLoose(["\n  font-size: 14px;\n  line-height: 22px;\n\n  &:not(:last-child) {\n    margin-bottom: 10px;\n  }\n\n  ", "\n"]);

  _templateObject24 = function _templateObject24() {
    return data;
  };

  return data;
}

function _templateObject23() {
  var data = _taggedTemplateLiteralLoose(["\n  max-width: 620px;\n"]);

  _templateObject23 = function _templateObject23() {
    return data;
  };

  return data;
}

function _templateObject22() {
  var data = _taggedTemplateLiteralLoose(["\n  word-break: break-word;\n  color: #b1b1b1;\n"]);

  _templateObject22 = function _templateObject22() {
    return data;
  };

  return data;
}

function _templateObject21() {
  var data = _taggedTemplateLiteralLoose(["\n      ", ";\n      min-height: 78px;\n    "]);

  _templateObject21 = function _templateObject21() {
    return data;
  };

  return data;
}

function _templateObject20() {
  var data = _taggedTemplateLiteralLoose(["\n    padding: 15px;\n  "]);

  _templateObject20 = function _templateObject20() {
    return data;
  };

  return data;
}

function _templateObject19() {
  var data = _taggedTemplateLiteralLoose(["\n  display: block;\n  padding: 15px 25px;\n  border-radius: 5px;\n  background-color: #000;\n\n  ", "\n\n  ", "\n"]);

  _templateObject19 = function _templateObject19() {
    return data;
  };

  return data;
}

function _templateObject18() {
  var data = _taggedTemplateLiteralLoose(["\n      ", ";\n      max-width: 350px;\n    "]);

  _templateObject18 = function _templateObject18() {
    return data;
  };

  return data;
}

function _templateObject17() {
  var data = _taggedTemplateLiteralLoose(["\n  display: block;\n  font-size: 24px;\n\n  &:not(:last-child) {\n    margin-bottom: 20px;\n  }\n\n  ", "\n"]);

  _templateObject17 = function _templateObject17() {
    return data;
  };

  return data;
}

function _templateObject16$1() {
  var data = _taggedTemplateLiteralLoose(["\n  &:not(:last-child) {\n    margin-bottom: 30px;\n  }\n"]);

  _templateObject16$1 = function _templateObject16() {
    return data;
  };

  return data;
}

function _templateObject15$1() {
  var data = _taggedTemplateLiteralLoose(["\n  display: inline-block;\n  margin-top: 5px;\n  text-decoration: underline;\n  color: #5566ff;\n"]);

  _templateObject15$1 = function _templateObject15() {
    return data;
  };

  return data;
}

function _templateObject14$1() {
  var data = _taggedTemplateLiteralLoose(["\n      ", ";\n      max-width: 500px;\n    "]);

  _templateObject14$1 = function _templateObject14() {
    return data;
  };

  return data;
}

function _templateObject13$1() {
  var data = _taggedTemplateLiteralLoose(["\n  position: relative;\n  display: block;\n  margin-top: 10px;\n  word-break: break-word;\n  color: #ff5555;\n\n  ", "\n"]);

  _templateObject13$1 = function _templateObject13() {
    return data;
  };

  return data;
}

function _templateObject12$1() {
  var data = _taggedTemplateLiteralLoose(["\n  display: block;\n  font-weight: 700;\n"]);

  _templateObject12$1 = function _templateObject12() {
    return data;
  };

  return data;
}

function _templateObject11$1() {
  var data = _taggedTemplateLiteralLoose(["\n  position: relative;\n  display: block;\n  margin-top: 10px;\n  word-break: break-all;\n  color: #999999;\n"]);

  _templateObject11$1 = function _templateObject11() {
    return data;
  };

  return data;
}

function _templateObject10$1() {
  var data = _taggedTemplateLiteralLoose(["\n  position: relative;\n  display: block;\n  font-size: 24px;\n  font-weight: 700;\n"]);

  _templateObject10$1 = function _templateObject10() {
    return data;
  };

  return data;
}

function _templateObject9$1() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n  min-height: 24px;\n  border-radius: 5px;\n"]);

  _templateObject9$1 = function _templateObject9() {
    return data;
  };

  return data;
}

function _templateObject8$1() {
  var data = _taggedTemplateLiteralLoose(["\n  position: relative;\n  background: #f6f7f8;\n  overflow: hidden;\n\n  &:after {\n    content: '';\n    position: absolute;\n    top: 0;\n    left: 0;\n    display: block;\n    width: 100%;\n    height: 100%;\n    background: #f6f7f8;\n    background: linear-gradient(to right, #eee 2%, #ddd 18%, #eee 33%);\n    background-size: 300%;\n    animation-name: ", ";\n    animation-duration: 1.5s;\n    animation-fill-mode: forwards;\n    animation-iteration-count: infinite;\n    animation-timing-function: linear;\n  }\n"]);

  _templateObject8$1 = function _templateObject8() {
    return data;
  };

  return data;
}

function _templateObject7$1() {
  var data = _taggedTemplateLiteralLoose(["\n  0%{\n    background-position: 50% 0;\n  }\n  100%{\n    background-position: -50% 0;\n  }\n"]);

  _templateObject7$1 = function _templateObject7() {
    return data;
  };

  return data;
}

function _templateObject6$2() {
  var data = _taggedTemplateLiteralLoose(["\n    padding: 35px 15px;\n  "]);

  _templateObject6$2 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5$2() {
  var data = _taggedTemplateLiteralLoose(["\n  padding: 35px 30px;\n\n  ", "\n"]);

  _templateObject5$2 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$5() {
  var data = _taggedTemplateLiteralLoose(["\n  overflow-y: auto;\n"]);

  _templateObject4$5 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$6() {
  var data = _taggedTemplateLiteralLoose(["\n  display: flex;\n  flex-direction: column;\n  width: 100%;\n  max-width: calc(100% - 20px);\n  max-height: calc(100vh - 20px);\n  margin: 0 auto;\n  border-top: 5px solid #ff5555;\n  border-radius: 5px;\n  font-size: 16px;\n  line-height: 24px;\n  background-color: #fff;\n  box-shadow: 0 5px 25px 1px rgba(0, 0, 0, 0.25);\n"]);

  _templateObject3$6 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$6() {
  var data = _taggedTemplateLiteralLoose(["\n  width: 100%;\n  max-width: 720px;\n"]);

  _templateObject2$6 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$9() {
  var data = _taggedTemplateLiteralLoose(["\n  position: fixed;\n  top: 0;\n  left: 0;\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  align-items: center;\n  width: 100%;\n  height: 100vh;\n  color: #262626;\n  background-color: rgb(180, 180, 180);\n"]);

  _templateObject$9 = function _templateObject() {
    return data;
  };

  return data;
}

function ErrorDevelop(_ref) {
  var _title;

  var getErrorDetails$1 = _ref.getErrorDetails,
      errorId = _ref.errorId,
      errorCode = _ref.errorCode,
      errorName = _ref.errorName;

  var _useState = useState({
    isLoading: !!errorId,
    errorTitle: null,
    error: null
  }),
      fetchingState = _useState[0],
      setFetchingState = _useState[1];

  var isLoading = fetchingState.isLoading,
      error = fetchingState.error,
      errorTitle = fetchingState.errorTitle;
  var errorDetailsRef = useRef(null);
  var errorDetailsCurrent = errorDetailsRef.current;
  var callStackLength = errorDetailsCurrent == null ? void 0 : errorDetailsCurrent.stacktrace.length;

  var _useState2 = useState(true),
      isCallStackCollapse = _useState2[0],
      setCallStackCollapse = _useState2[1];

  var _useState3 = useState([]),
      openedStackTraceCodeLines = _useState3[0],
      setOpenedStackTraceCodeLines = _useState3[1];

  var callStackAvailableLength = !isCallStackCollapse ? callStackLength : 3;

  function expandCallStack() {
    setCallStackCollapse(false);
  }

  function expandViewCode(index) {
    if (openedStackTraceCodeLines.indexOf(index) === -1) {
      setOpenedStackTraceCodeLines([...openedStackTraceCodeLines, index]);
    } else {
      setOpenedStackTraceCodeLines(openedStackTraceCodeLines.filter(item => item !== index));
    }
  }

  var errorDetailsPromise = getErrorDetails$1 ? getErrorDetails$1 : getErrorDetails;
  useEffect(() => {
    if (!errorId) return;
    if (error || errorDetailsCurrent) return;
    errorDetailsPromise(errorId).then(response => {
      errorDetailsRef.current = response;
      setFetchingState(prevFetchingState => _extends({}, prevFetchingState, {
        isLoading: false
      }));
    }).catch(error => {
      var _error$status, _error$body;

      var code = (_error$status = error.status) == null ? void 0 : _error$status.code;
      var message = (_error$body = error.body) == null ? void 0 : _error$body.message;
      setFetchingState({
        error: getFailureMessage(code, message),
        errorTitle: code !== 404 ? 'Tager Sentry Response:' : null,
        isLoading: false
      });
    });
  }, [error, errorDetailsCurrent, errorId]);
  var title, sentryUrl, file, stacktrace;

  if (errorDetailsCurrent) {
    title = errorDetailsCurrent.title;
    sentryUrl = errorDetailsCurrent.sentryUrl;
    file = errorDetailsCurrent.file;
    stacktrace = errorDetailsCurrent.stacktrace;
  }

  return React__default.createElement(Container$3, null, React__default.createElement(Inner$2, null, React__default.createElement(Card, null, React__default.createElement(CardScroll, null, React__default.createElement(CardInner, null, React__default.createElement(Block, null, React__default.createElement(ErrorName, null, !isLoading ? errorCode + " - " + errorName : "Loading error details..."), !isLoading && !errorDetailsCurrent && errorId ? React__default.createElement(ErrorID, null, "Error ID: ", errorId) : null, isLoading || title || error ? React__default.createElement(ErrorMessage, {
    isLoading: isLoading
  }, errorTitle ? React__default.createElement(ErrorMessageTitle, null, errorTitle) : null, (_title = title) != null ? _title : error) : null, sentryUrl ? React__default.createElement(GetDetailsLink, {
    href: sentryUrl,
    target: "_blank"
  }, "View on Sentry") : null), isLoading || file ? React__default.createElement(Block, null, React__default.createElement(Title, {
    isLoading: isLoading
  }, "Source"), React__default.createElement(SourceCode, {
    isLoading: isLoading
  }, React__default.createElement(File, null, file))) : null, isLoading || stacktrace ? React__default.createElement(Block, null, React__default.createElement(Title, {
    isLoading: isLoading
  }, "Call Stack"), stacktrace ? React__default.createElement(CallStack, null, stacktrace.slice(0, callStackAvailableLength).map((item, index) => React__default.createElement(CallStackItem, {
    key: index,
    isLoading: isLoading
  }, React__default.createElement(CallStackFunction, null, index + 1, ". ", item.function), React__default.createElement(CallStackFile, null, item.file, " -", ' ', React__default.createElement(CallStackFileLine, null, "Line ", item.line, ", Col ", item.col)), React__default.createElement("br", null), React__default.createElement(CallStackButton, {
    onClick: () => expandViewCode(index)
  }, openedStackTraceCodeLines.indexOf(index) !== -1 ? 'Hide code' : 'View code'), openedStackTraceCodeLines.indexOf(index) !== -1 ? React__default.createElement(CallStackCode, null, item.code.map(codeLine => React__default.createElement(CallStackCodeLine, {
    isActive: codeLine.line === item.line
  }, codeLine.line, codeLine.code))) : null)), isCallStackCollapse && stacktrace.length > 3 ? React__default.createElement(CallStackButton, {
    onClick: expandCallStack
  }, "Show collapsed frames") : null) : React__default.createElement(CallStack, null, generateNumberArray(3).map(num => React__default.createElement(CallStackItem, {
    key: num,
    isLoading: isLoading
  })))) : null)), React__default.createElement(ErrorCardFooter, null))));
}

var Container$3 = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject$9());
var Inner$2 = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject2$6());
var Card = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject3$6());
var CardScroll = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject4$5());
var CardInner = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject5$2(), /*#__PURE__*/media.mobile( /*#__PURE__*/css( /*#__PURE__*/_templateObject6$2())));
var loadingPlaceholderAnimation = /*#__PURE__*/keyframes( /*#__PURE__*/_templateObject7$1());
var loadingPlaceholderCss = /*#__PURE__*/css( /*#__PURE__*/_templateObject8$1(), loadingPlaceholderAnimation);
var preloader = /*#__PURE__*/css( /*#__PURE__*/_templateObject9$1(), loadingPlaceholderCss);
var ErrorName = /*#__PURE__*/styled.span( /*#__PURE__*/_templateObject10$1());
var ErrorID = /*#__PURE__*/styled.span( /*#__PURE__*/_templateObject11$1());
var ErrorMessageTitle = /*#__PURE__*/styled.span( /*#__PURE__*/_templateObject12$1());
var ErrorMessage = /*#__PURE__*/styled.span( /*#__PURE__*/_templateObject13$1(), (_ref2) => {
  var isLoading = _ref2.isLoading;
  return isLoading && css(_templateObject14$1(), preloader);
});
var GetDetailsLink = /*#__PURE__*/styled.a( /*#__PURE__*/_templateObject15$1());
var Block = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject16$1());
var Title = /*#__PURE__*/styled.span( /*#__PURE__*/_templateObject17(), (_ref3) => {
  var isLoading = _ref3.isLoading;
  return isLoading && css(_templateObject18(), preloader);
});
var SourceCode = /*#__PURE__*/styled.code( /*#__PURE__*/_templateObject19(), /*#__PURE__*/media.mobile( /*#__PURE__*/css( /*#__PURE__*/_templateObject20())), (_ref4) => {
  var isLoading = _ref4.isLoading;
  return isLoading && css(_templateObject21(), preloader);
});
var File = /*#__PURE__*/styled.span( /*#__PURE__*/_templateObject22());
var CallStack = /*#__PURE__*/styled.ul( /*#__PURE__*/_templateObject23());
var CallStackItem = /*#__PURE__*/styled.li( /*#__PURE__*/_templateObject24(), (_ref5) => {
  var isLoading = _ref5.isLoading;
  return isLoading && css(_templateObject25(), preloader);
});
var CallStackFile = /*#__PURE__*/styled.span( /*#__PURE__*/_templateObject26());
var CallStackFileLine = /*#__PURE__*/styled.span( /*#__PURE__*/_templateObject27());
var CallStackFunction = /*#__PURE__*/styled.span( /*#__PURE__*/_templateObject28());
var CallStackCode = /*#__PURE__*/styled.code( /*#__PURE__*/_templateObject29());
var CallStackButton = /*#__PURE__*/styled.code( /*#__PURE__*/_templateObject30());
var CallStackCodeLine = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject31(), (_ref6) => {
  var isActive = _ref6.isActive;
  return isActive ? '#ececec' : 'transparent';
});

function _templateObject13$2() {
  var data = _taggedTemplateLiteralLoose(["\n    margin-top: 20px;\n    font-size: 12px;\n  "]);

  _templateObject13$2 = function _templateObject13() {
    return data;
  };

  return data;
}

function _templateObject12$2() {
  var data = _taggedTemplateLiteralLoose(["\n    margin-top: 35px;\n    font-size: 13px;\n  "]);

  _templateObject12$2 = function _templateObject12() {
    return data;
  };

  return data;
}

function _templateObject11$2() {
  var data = _taggedTemplateLiteralLoose(["\n  max-width: 100%;\n  margin-top: 50px;\n  font-size: 15px;\n  color: #aaaaaa;\n\n  ", "\n\n  ", "\n"]);

  _templateObject11$2 = function _templateObject11() {
    return data;
  };

  return data;
}

function _templateObject10$2() {
  var data = _taggedTemplateLiteralLoose(["\n    font-size: 20px;\n  "]);

  _templateObject10$2 = function _templateObject10() {
    return data;
  };

  return data;
}

function _templateObject9$2() {
  var data = _taggedTemplateLiteralLoose(["\n    font-size: 30px;\n  "]);

  _templateObject9$2 = function _templateObject9() {
    return data;
  };

  return data;
}

function _templateObject8$2() {
  var data = _taggedTemplateLiteralLoose(["\n    font-size: 40px;\n  "]);

  _templateObject8$2 = function _templateObject8() {
    return data;
  };

  return data;
}

function _templateObject7$2() {
  var data = _taggedTemplateLiteralLoose(["\n  max-width: 100%;\n  margin-top: 15px;\n  font-size: 60px;\n  text-transform: uppercase;\n  letter-spacing: 0.1em;\n  color: #000;\n\n  ", "\n\n  ", "\n  \n  ", "\n"]);

  _templateObject7$2 = function _templateObject7() {
    return data;
  };

  return data;
}

function _templateObject6$3() {
  var data = _taggedTemplateLiteralLoose(["\n    font-size: 100px;\n  "]);

  _templateObject6$3 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5$3() {
  var data = _taggedTemplateLiteralLoose(["\n    font-size: 130px;\n  "]);

  _templateObject5$3 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$6() {
  var data = _taggedTemplateLiteralLoose(["\n    font-size: 160px;\n  "]);

  _templateObject4$6 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$7() {
  var data = _taggedTemplateLiteralLoose(["\n  max-width: 100%;\n  font-size: 180px;\n  font-weight: 900;\n  line-height: 1;\n  text-transform: uppercase;\n  text-shadow: 0 0 1px #000;\n\n  ", "\n\n  ", "\n  \n  ", "\n"]);

  _templateObject3$7 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$7() {
  var data = _taggedTemplateLiteralLoose(["\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  align-items: center;\n  max-width: 960px;\n"]);

  _templateObject2$7 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$a() {
  var data = _taggedTemplateLiteralLoose(["\n  position: fixed;\n  top: 0;\n  left: 0;\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  align-items: center;\n  width: 100%;\n  height: 100vh;\n  padding-bottom: 35px;\n  text-align: center;\n  color: #262626;\n  background-color: rgb(245, 245, 245);\n"]);

  _templateObject$a = function _templateObject() {
    return data;
  };

  return data;
}

function ErrorProd(_ref) {
  var errorCode = _ref.errorCode,
      errorName = _ref.errorName,
      errorId = _ref.errorId;
  return React__default.createElement(Container$4, null, React__default.createElement(Inner$3, null, React__default.createElement(Code, null, errorCode), React__default.createElement(Name, null, errorName), errorId ? React__default.createElement(EventId, null, "Error ID: ", errorId) : null));
}

var Container$4 = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject$a());
var Inner$3 = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject2$7());
var Code = /*#__PURE__*/styled.span( /*#__PURE__*/_templateObject3$7(), /*#__PURE__*/media.tabletLarge( /*#__PURE__*/css( /*#__PURE__*/_templateObject4$6())), /*#__PURE__*/media.tabletSmall( /*#__PURE__*/css( /*#__PURE__*/_templateObject5$3())), /*#__PURE__*/media.mobile( /*#__PURE__*/css( /*#__PURE__*/_templateObject6$3())));
var Name = /*#__PURE__*/styled.span( /*#__PURE__*/_templateObject7$2(), /*#__PURE__*/media.tabletLarge( /*#__PURE__*/css( /*#__PURE__*/_templateObject8$2())), /*#__PURE__*/media.tabletSmall( /*#__PURE__*/css( /*#__PURE__*/_templateObject9$2())), /*#__PURE__*/media.mobile( /*#__PURE__*/css( /*#__PURE__*/_templateObject10$2())));
var EventId = /*#__PURE__*/styled.span( /*#__PURE__*/_templateObject11$2(), /*#__PURE__*/media.tabletSmall( /*#__PURE__*/css( /*#__PURE__*/_templateObject12$2())), /*#__PURE__*/media.mobile( /*#__PURE__*/css( /*#__PURE__*/_templateObject13$2())));

function getErrorName(code) {
  var codeString = String(code);
  if (codeString === '404') return 'Page Not Found';
  return 'Server Error';
}

function Error$1(_ref) {
  var getErrorDetails = _ref.getErrorDetails,
      errorCode = _ref.errorCode,
      errorName = _ref.errorName,
      errorId = _ref.errorId;
  var isDevelopment = process.env.NEXT_PUBLIC_ENV === 'development';
  return isDevelopment ? React__default.createElement(ErrorDevelop, {
    errorCode: errorCode,
    errorName: errorName,
    errorId: errorId,
    getErrorDetails: getErrorDetails
  }) : React__default.createElement(ErrorProd, {
    errorCode: errorCode,
    errorName: getErrorName(errorCode),
    errorId: errorId
  });
}

export { Error$1 as ErrorModule, GalleryProvider, Image$1 as Image, LoadableImage, Overlay as ModalOverlay, ModalProvider, Page, Preloader, Spinner, createLinkComponent, createMediaMixin, createMediaQuery, createPictureComponent, createPlainPictureComponent, placeholderAnimation, placeholderAnimationCss, useGallery, useModal, visuallyHidden };
//# sourceMappingURL=web-components.esm.js.map
